// src/components/Reports.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import Header from './Header';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Import Excel export libraries
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// Import DOCX export library
import { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType, AlignmentType, TextRun, HeadingLevel, BorderStyle } from 'docx';
// Import Roboto font để sử dụng với PDF export
import '../fonts/Roboto-Regular-normal.js';
import '../fonts/Roboto-Bold-normal.js';

// Đăng ký ChartJS
ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement,
    ArcElement, Title, Tooltip, Legend
);

// --- Helper Functions ---

const getDateFromTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Nếu đã là đối tượng Date
    if (timestamp instanceof Date) return timestamp;
    
    // Nếu là Firebase Timestamp
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    
    // Nếu là định dạng { _seconds, _nanoseconds }
    if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
    }
    
    // Nếu là đối tượng { seconds, nanoseconds } (định dạng cũ của Firebase)
    if (timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    
    // Nếu là string hoặc timestamp dạng số
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
};

const formatDate = (date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = getDateFromTimestamp(date);
    if (!d) return 'N/A';

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    if (includeTime) {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return `${day}/${month}/${year}`;
};

const calculateDueDate = (startDate, duration) => {
    const start = getDateFromTimestamp(startDate);
    if (!start) return null;
    const durationDays = parseInt(duration);
    if (isNaN(durationDays)) return null;
    const dueDate = new Date(start);
    dueDate.setDate(dueDate.getDate() + durationDays);
    dueDate.setHours(23, 59, 59, 999);
    return dueDate;
};

const isTaskOverdue = (task) => {
    if (!task || !task.start_date || !task.duration || task.status === 'completed' || task.status === 'canceled') {
        return false;
    }
    const dueDate = calculateDueDate(task.start_date, task.duration);
    if (!dueDate) return false;
    const today = new Date();
    return dueDate < today;
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'notStarted': case 'not-started': return 'Chưa bắt đầu';
        case 'inProgress': case 'in-progress': return 'Đang thực hiện';
        case 'pendingApproval': return 'Chờ phê duyệt';
        case 'approved': return 'Đã phê duyệt';
        case 'completed': return 'Đã hoàn thành';
        case 'canceled': return 'Đã hủy';
        default: return 'Không xác định';
    }
};

const getPriorityLabel = (priority) => {
    switch (priority) {
        case 'low': return 'Thấp';
        case 'medium': return 'Trung bình';
        case 'high': return 'Cao';
        default: return 'Trung bình';
    }
};

const formatAssigneesList = (assignees) => {
    if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
        return <li className="text-sm italic text-gray-500 dark:text-gray-400">Chưa giao</li>;
    }
    return assignees.map(a => (
        <li key={a.uid || a.email} className="text-sm">- {a.displayName || a.email || 'N/A'}</li>
    ));
};

const getAssigneeNames = (assignees) => {
    if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
        return 'Chưa giao';
    }
    return assignees.map(a => a.displayName || a.email || 'N/A').join(', ');
};

const getAssigneeNamesJSX = (assignees) => {
    if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
        return <span className="text-xs italic text-gray-500 dark:text-gray-400">Chưa giao</span>;
    }
    return assignees.map(a => a.displayName || a.email || 'N/A').join(', ');
};

const getAssigneeNamesCSV = (assignees) => {
    if (!assignees || !Array.isArray(assignees) || assignees.length === 0) return '';
    return assignees.map(a => (a.displayName || a.email || 'N/A').replace(/"/g, '""')).join('; ');
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// --- New Helper Function for Link Detection ---
const Linkify = ({ text }) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return (
        <>
            {parts.map((part, index) => {
                if (part.match(urlRegex)) {
                    return (
                        <a
                            key={index}
                            href={part}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                            {part}
                        </a>
                    );
                } else {
                    return <span key={index}>{part}</span>;
                }
            })}
        </>
    );
};

// Định dạng ngày giờ thống nhất theo kiểu "03/05/2025 19:06"
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    try {
        // Nếu là Firebase Timestamp
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } 
        // Nếu là định dạng { _seconds, _nanoseconds }
        else if (timestamp._seconds !== undefined) {
            date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
        }
        // Nếu là định dạng { seconds, nanoseconds }
        else if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
        }
        // Nếu là timestamp dạng số hoặc string
        else if (typeof timestamp === 'number' || typeof timestamp === 'string') {
            date = new Date(timestamp);
        }
        // Nếu đã là đối tượng Date
        else if (timestamp instanceof Date) {
            date = timestamp;
        }
        
        if (!date || isNaN(date.getTime())) return 'N/A';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (err) {
        console.error('Error formatting date:', err);
        return 'N/A';
    }
};

// Hàm chuyển đổi role sang tiếng Việt
const getRoleLabel = (role) => {
    if (!role) return '';
    switch (role) {
        case 'executor':
        case 'assignee':
        case 'Người thực hiện':
            return 'Người thực hiện';
        case 'collaborator':
        case 'Người phối hợp':
            return 'Người phối hợp';
        case 'supervisor':
        case 'Người giám sát':
            return 'Người giám sát';
        case 'approver':
        case 'Người phê duyệt':
            return 'Người phê duyệt';
        case 'member':
        case 'Thành viên':
            return 'Thành viên';
        default:
            return role;
    }
};

// Helper xác định vai trò của user trong task (trả về nhãn tiếng Việt)
const getUserRoleInTask = (task, currentUser) => {
    if (!task || !currentUser) return null;
    if (Array.isArray(task.assignees)) {
        const found = task.assignees.find(a => a.uid === currentUser.uid);
        if (found && found.role) return getRoleLabel(found.role);
        if (found) return 'Người thực hiện';
    }
    if (Array.isArray(task.participants)) {
        const found = task.participants.find(a => a.uid === currentUser.uid);
        if (found && found.role) return getRoleLabel(found.role);
        if (found) return 'Thành viên';
    }
    if (task.approverId === currentUser.uid) return 'Người phê duyệt';
    return null;
};

// Nâng cấp getActionLabel để nhận diện nhiều hành động hơn và sinh mô tả chuyên nghiệp
const getActionLabel = (action, details, logType) => {
    if (!action && logType) {
        // Nếu không có action, dùng logType
        switch (logType) {
            case 'status_update':
            case 'status_change':
                if (details && details.from && details.to) {
                    return `Chuyển trạng thái từ ${getStatusLabel(details.from)} sang ${getStatusLabel(details.to)}`;
                }
                return 'Chuyển trạng thái công việc';
            case 'progress_update':
                return 'Cập nhật tiến độ công việc';
            case 'priority_change':
                return 'Thay đổi mức ưu tiên công việc';
            case 'comment':
            case 'comment_added':
                return 'Đã bình luận';
            case 'file_upload':
            case 'file_uploaded':
                return 'Tải lên tệp tin';
            case 'file_delete':
            case 'file_deleted':
                return 'Xóa tệp tin';
            case 'assignment':
            case 'assignee_added':
                return 'Phân công người thực hiện';
            case 'assignee_removed':
                return 'Gỡ người thực hiện';
            case 'task_create':
                return 'Tạo công việc mới';
            case 'task_delete':
                return 'Xóa công việc';
            case 'task_edit':
            case 'field_update':
                return 'Cập nhật công việc';
            case 'due_date_change':
                return 'Thay đổi hạn chót';
            case 'completion':
                return 'Đánh dấu hoàn thành';
            case 'approval':
                return 'Phê duyệt công việc';
            case 'rejection':
                return 'Từ chối công việc';
            case 'cancellation':
                return 'Hủy công việc';
            default:
                // Nếu type là tiếng Anh, chuyển sang tiếng Việt chung
                return 'Thực hiện hành động: ' + (logType.replace(/_/g, ' '));
        }
    }
    // Việt hóa các action phổ biến còn sót
    switch (action) {
        case 'member_added':
        case 'member add':
        case 'member added':
            return 'Thêm thành viên vào phòng ban';
        case 'member_removed':
        case 'member remove':
        case 'member removed':
            return 'Gỡ thành viên khỏi phòng ban';
        case 'team_created':
        case 'team create':
        case 'team created':
            return 'Tạo phòng ban mới';
        case 'description_updated':
        case 'description update':
        case 'description updated':
            return 'Cập nhật mô tả phòng ban';
        case 'name_updated':
        case 'name update':
        case 'name updated':
            return 'Cập nhật tên phòng ban';
        case 'update':
            return 'Cập nhật thông tin';
        case 'task_created':
        case 'task create':
        case 'task created':
            return 'Tạo công việc mới';
        case 'task_deleted':
        case 'task delete':
        case 'task deleted':
            return 'Xóa công việc';
        case 'task_updated':
        case 'task update':
        case 'task updated':
            return 'Cập nhật công việc';
        case 'status_changed':
        case 'status change':
        case 'status changed':
            if (details && details.from && details.to) {
                return `Chuyển trạng thái từ ${getStatusLabel(details.from)} sang ${getStatusLabel(details.to)}`;
            }
            return 'Thay đổi trạng thái công việc';
        default:
            // Việt hóa các action dạng "xxx updated", "xxx added", "xxx created", "xxx removed", "xxx deleted"
            if (typeof action === 'string') {
                if (/updated$/.test(action)) return 'Cập nhật ' + action.replace(/_?updated$/, '').replace(/_/g, ' ').trim();
                if (/added$/.test(action)) return 'Thêm ' + action.replace(/_?added$/, '').replace(/_/g, ' ').trim();
                if (/created$/.test(action)) return 'Tạo ' + action.replace(/_?created$/, '').replace(/_/g, ' ').trim();
                if (/removed$/.test(action)) return 'Gỡ ' + action.replace(/_?removed$/, '').replace(/_/g, ' ').trim();
                if (/deleted$/.test(action)) return 'Xóa ' + action.replace(/_?deleted$/, '').replace(/_/g, ' ').trim();
            }
            // Nếu không xác định được, trả về 'Hành động khác'
            return 'Hành động khác';
    }
};

// Nâng cấp getActivityDescription để luôn trả về mô tả cụ thể
const getActivityDescription = (log) => {
    switch (log.type) {
        case 'status_update':
        case 'status_change':
            return (
                <span>
                    Chuyển trạng thái từ <strong>{getStatusLabel(log.oldStatus || log.details?.from)}</strong> sang <strong>{getStatusLabel(log.newStatus || log.details?.to)}</strong>
                </span>
            );
        case 'progress_update':
            return (
                <span>
                    Đã cập nhật tiến độ từ <strong>{log.oldProgress || log.details?.from}%</strong> lên <strong>{log.newProgress || log.details?.to}%</strong>
                </span>
            );
        case 'priority_change':
            return (
                <span>
                    Đã thay đổi mức ưu tiên từ <strong>{getPriorityLabel(log.oldPriority)}</strong> sang <strong>{getPriorityLabel(log.newPriority)}</strong>
                </span>
            );
        case 'comment':
        case 'comment_added':
            return (
                <span>
                    Đã bình luận: <span className="italic">{log.content || log.details?.commentText}</span>
                </span>
            );
        case 'file_upload':
        case 'file_uploaded':
            return (
                <span>
                    Đã tải lên tệp <span className="text-blue-600 dark:text-blue-400">{log.fileName || log.details?.fileName}</span> ({formatFileSize(log.fileSize || log.details?.fileSize || 0)})
                </span>
            );
        case 'file_delete':
        case 'file_deleted':
            return (
                <span>
                    Đã xóa tệp <span className="line-through">{log.fileName || log.details?.fileName}</span>
                </span>
            );
        case 'assignment':
        case 'assignee_added':
            return (
                <span>
                    Đã phân công <strong>{log.assignedUsers?.map(u => u.displayName || u.email).join(', ') || log.details?.added?.map(u => u.displayName || u.email).join(', ')}</strong> vào công việc
                </span>
            );
        case 'assignee_removed':
            return (
                <span>
                    Đã gỡ <strong>{log.assignedUsers?.map(u => u.displayName || u.email).join(', ') || log.details?.removed?.map(u => u.displayName || u.email).join(', ')}</strong> khỏi công việc
                </span>
            );
        case 'task_create':
            return <span>Đã tạo công việc mới</span>;
        case 'task_delete':
            return <span>Đã xóa công việc</span>;
        case 'task_edit':
        case 'field_update':
            return <span>Đã cập nhật công việc</span>;
        case 'due_date_change':
            return (
                <span>
                    Đã thay đổi hạn chót từ <strong>{formatDate(log.oldDueDate)}</strong> sang <strong>{formatDate(log.newDueDate)}</strong>
                </span>
            );
        case 'completion':
            return <span>Đánh dấu công việc là hoàn thành</span>;
        case 'approval':
            return <span>Đã phê duyệt công việc</span>;
        case 'rejection':
            return <span>Đã từ chối công việc</span>;
        case 'cancellation':
            return <span>Đã hủy công việc</span>;
        default: {
            // Ưu tiên Việt hóa action nếu có
            if (log.action || log.type) {
                return <span>{getActionLabel(log.action, log.details, log.type)}</span>;
            }
            if (log.details && typeof log.details === 'string') {
                return <span>{log.details}</span>;
            }
            if (log.description) {
                return <span>{log.description}</span>;
            }
            // Nếu vẫn chưa rõ, hiển thị "Hành động không xác định"
            return <span>Hành động không xác định</span>;
        }
    }
};

// Activity Log Table Component
const ActivityLogTable = ({ filteredTasks, teamActivityLogs, handleViewTaskDetailsById }) => {
    const [allActivityLogs, setAllActivityLogs] = useState([]);
    const [displayedLogs, setDisplayedLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [logsPerPage] = useState(10);
    const [selectedTask, setSelectedTask] = useState('all');
    const [selectedActivityType, setSelectedActivityType] = useState('all');
    const [selectedLogType, setSelectedLogType] = useState('all'); // NEW: filter for log type
    const [sortOrder, setSortOrder] = useState('newest');
    
    // Trích xuất tất cả nhật ký hoạt động từ các công việc và team
    useEffect(() => {
        let logs = [];
        // Task logs
        if (filteredTasks && filteredTasks.length > 0) {
            filteredTasks.forEach(task => {
                const taskName = task.text || task.title || 'Không xác định';
                if (task.activityLogs && Array.isArray(task.activityLogs)) {
                    task.activityLogs.forEach(log => {
                        logs.push({
                            ...log,
                            id: `${task.id}_${log.timestamp ? log.timestamp.seconds || log.timestamp._seconds || Date.now() : Date.now()}`,
                            taskId: task.id,
                            taskName,
                            typeLog: 'task',
                        });
                    });
                }
                if (task.statusUpdates && Array.isArray(task.statusUpdates)) {
                    task.statusUpdates.forEach(update => {
                        logs.push({
                            id: `status_${task.id}_${update.timestamp ? update.timestamp.seconds || update.timestamp._seconds || Date.now() : Date.now()}`,
                            type: 'status_update',
                            timestamp: update.timestamp,
                            userDisplayName: update.by?.displayName,
                            userEmail: update.by?.email,
                            userPhotoURL: update.by?.photoURL,
                            userId: update.by?.uid,
                            oldStatus: update.from,
                            newStatus: update.to,
                            taskId: task.id,
                            taskName,
                            typeLog: 'task',
                        });
                    });
                }
                if (task.comments && Array.isArray(task.comments)) {
                    task.comments.forEach(comment => {
                        logs.push({
                            id: `comment_${comment.id || task.id}_${(comment.timestamp ? comment.timestamp.seconds || comment.timestamp._seconds : (comment.createdAt ? comment.createdAt.seconds || comment.createdAt._seconds : Date.now()))}`,
                            type: 'comment',
                            timestamp: comment.timestamp || comment.createdAt || comment.date,
                            userDisplayName: comment.user?.displayName || comment.createdBy?.displayName || comment.author?.displayName || comment.user?.email || comment.createdBy?.email || comment.author?.email,
                            userEmail: comment.user?.email || comment.createdBy?.email || comment.author?.email,
                            userPhotoURL: comment.user?.photoURL || comment.createdBy?.photoURL || comment.author?.photoURL,
                            userId: comment.user?.uid || comment.createdBy?.uid || comment.author?.uid,
                            content: comment.text,
                            taskId: task.id,
                            taskName,
                            typeLog: 'task',
                        });
                    });
                }
                if (task.files && Array.isArray(task.files)) {
                    task.files.forEach(file => {
                        logs.push({
                            id: `file_${file.id || task.id}_${file.uploadedAt ? file.uploadedAt.seconds || file.uploadedAt._seconds || Date.now() : Date.now()}`,
                            type: 'file_upload',
                            timestamp: file.uploadedAt,
                            userDisplayName: file.uploadedBy?.displayName,
                            userEmail: file.uploadedBy?.email,
                            userPhotoURL: file.uploadedBy?.photoURL,
                            userId: file.uploadedBy?.uid,
                            fileName: file.name,
                            fileUrl: file.url,
                            fileSize: file.size,
                            taskId: task.id,
                            taskName,
                            typeLog: 'task',
                        });
                    });
                }
                if (task.assignmentHistory && Array.isArray(task.assignmentHistory)) {
                    task.assignmentHistory.forEach(assignment => {
                        logs.push({
                            id: `assignment_${task.id}_${assignment.timestamp ? assignment.timestamp.seconds || assignment.timestamp._seconds || Date.now() : Date.now()}`,
                            type: 'assignment',
                            timestamp: assignment.timestamp,
                            userDisplayName: assignment.by?.displayName,
                            userEmail: assignment.by?.email,
                            userPhotoURL: assignment.by?.photoURL,
                            userId: assignment.by?.uid,
                            assignedUsers: assignment.assignedTo,
                            assignmentAction: assignment.action,
                            taskId: task.id,
                            taskName,
                            typeLog: 'task',
                        });
                    });
                }
            });
        }
        // Team logs
        if (teamActivityLogs && teamActivityLogs.length > 0) {
            teamActivityLogs.forEach(teamLog => {
                logs.push({
                    ...teamLog,
                    id: `team_${teamLog.teamId}_${teamLog.timestamp ? teamLog.timestamp.seconds || teamLog.timestamp._seconds || Date.now() : Date.now()}`,
                    typeLog: 'team',
                });
            });
        }
        const sortedLogs = logs.sort((a, b) => {
            const timeA = a.timestamp ? (a.timestamp.seconds || a.timestamp._seconds || 0) : 0;
            const timeB = b.timestamp ? (b.timestamp.seconds || b.timestamp._seconds || 0) : 0;
            return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
        });
        setAllActivityLogs(sortedLogs);
        setDisplayedLogs(sortedLogs);
        setLoading(false);
    }, [filteredTasks, teamActivityLogs, sortOrder]);

    // Xử lý bộ lọc và sắp xếp
    const applyFilters = (logs, taskFilter, typeFilter, logTypeFilter, order) => {
        let result = [...logs];
        if (logTypeFilter !== 'all') {
            result = result.filter(log => log.typeLog === logTypeFilter);
        }
        if (taskFilter !== 'all') {
            result = result.filter(log => log.taskId === taskFilter);
        }
        if (typeFilter !== 'all') {
            result = result.filter(log => log.type === typeFilter);
        }
        result = result.sort((a, b) => {
            const timeA = a.timestamp ? (a.timestamp.seconds || a.timestamp._seconds || 0) : 0;
            const timeB = b.timestamp ? (b.timestamp.seconds || b.timestamp._seconds || 0) : 0;
            return order === 'newest' ? timeB - timeA : timeA - timeB;
        });
        return result;
    };
    useEffect(() => {
        const filtered = applyFilters(allActivityLogs, selectedTask, selectedActivityType, selectedLogType, sortOrder);
        setDisplayedLogs(filtered);
        setCurrentPage(1);
    }, [allActivityLogs, selectedTask, selectedActivityType, selectedLogType, sortOrder]);
    
    const toggleSortOrder = () => {
        setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest');
    };
    
    const indexOfLastLog = currentPage * logsPerPage;
    const indexOfFirstLog = indexOfLastLog - logsPerPage;
    const currentLogs = displayedLogs.slice(indexOfFirstLog, indexOfLastLog);
    const totalPages = Math.ceil(displayedLogs.length / logsPerPage);
    
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    
    const getActivityIcon = (type) => {
        switch (type) {
            case 'status_update': return 'fas fa-exchange-alt text-blue-500';
            case 'comment': return 'fas fa-comment text-green-500';
            case 'file_upload': return 'fas fa-file-upload text-indigo-500';
            case 'file_delete': return 'fas fa-file-excel text-red-500';
            case 'assignment': return 'fas fa-user-plus text-yellow-500';
            case 'task_create': return 'fas fa-plus-circle text-green-500';
            case 'task_delete': return 'fas fa-trash text-red-500';
            case 'task_edit': return 'fas fa-edit text-blue-500';
            default: return 'fas fa-history text-gray-500';
        }
    };
    
    // Lấy tên người dùng cho mọi loại log, đặc biệt là comment
    const getUserDisplayName = (log) => {
        return (
            log.userDisplayName ||
            log.userEmail ||
            log.user?.displayName ||
            log.user?.email ||
            log.createdBy?.displayName ||
            log.createdBy?.email ||
            log.author?.displayName ||
            log.author?.email ||
            (typeof log.createdBy === 'string' ? log.createdBy : null) ||
            (typeof log.author === 'string' ? log.author : null) ||
            'Người dùng không xác định'
        );
    };

    // Lấy avatar
    const getUserPhotoURL = (log) => {
        return (
            log.userPhotoURL ||
            log.user?.photoURL ||
            log.createdBy?.photoURL ||
            log.author?.photoURL ||
            null
        );
    };

    // Lấy thời gian
    const getLogTimestamp = (log) => {
        return log.timestamp || log.createdAt || log.date || null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white"></div>
            </div>
        );
    }
    
    if (allActivityLogs.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="text-center text-gray-500 dark:text-gray-400">
                    <p>Không có nhật ký hoạt động nào được ghi nhận.</p>
                </div>
            </div>
        );
    }
    
    return (
        <>
            <div className="flex flex-wrap justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                {/* Bộ lọc loại log */}
                <div className="inline-flex items-center">
                    <label className="mr-2 text-sm font-medium text-gray-600 dark:text-gray-300">Loại nhật ký:</label>
                    <select
                        value={selectedLogType}
                        onChange={e => setSelectedLogType(e.target.value)}
                        className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                    >
                        <option value="all">Tất cả</option>
                        <option value="task">Nhật ký công việc</option>
                        <option value="team">Nhật ký phòng ban</option>
                    </select>
                </div>
                {/* Bộ lọc công việc */}
                <div className="inline-flex items-center">
                    <label className="mr-2 text-sm font-medium text-gray-600 dark:text-gray-300">Công việc:</label>
                    <select
                        value={selectedTask}
                        onChange={(e) => setSelectedTask(e.target.value)}
                        className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                    >
                        <option value="all">Tất cả công việc</option>
                        {Array.from(new Set(allActivityLogs.map(log => log.taskId))).map(taskId => {
                            const taskName = allActivityLogs.find(log => log.taskId === taskId)?.taskName || 'Công việc không tên';
                            return (
                                <option key={taskId} value={taskId}>
                                    {taskName}
                                </option>
                            );
                        })}
                    </select>
                </div>
                
                {/* Bộ lọc loại hoạt động */}
                <div className="inline-flex items-center">
                    <label className="mr-2 text-sm font-medium text-gray-600 dark:text-gray-300">Loại hoạt động:</label>
                    <select
                        value={selectedActivityType}
                        onChange={(e) => setSelectedActivityType(e.target.value)}
                        className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                    >
                        <option value="all">Tất cả hoạt động</option>
                        <option value="status_update">Chuyển trạng thái</option>
                        <option value="comment">Bình luận</option>
                        <option value="file_upload">Tải lên tệp tin</option>
                        <option value="file_delete">Xóa tệp tin</option>
                        <option value="assignment">Phân công</option>
                        <option value="task_create">Tạo công việc</option>
                        <option value="task_edit">Sửa công việc</option>
                        <option value="task_delete">Xóa công việc</option>
                    </select>
                </div>
                
                {/* Thứ tự sắp xếp */}
                <div>
                    <button 
                        onClick={toggleSortOrder} 
                        className="text-xs inline-flex items-center px-3 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                        <i className={`fas fa-sort-${sortOrder === 'newest' ? 'down' : 'up'} mr-2`}></i>
                        {sortOrder === 'newest' ? 'Mới nhất trước' : 'Cũ nhất trước'}
                    </button>
                </div>
            </div>
            
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Thời gian</th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Người dùng</th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hành động</th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Công việc</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {currentLogs.length > 0 ? (
                            currentLogs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(getLogTimestamp(log))}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">
                                        <div className="flex items-center space-x-2">
                                            <img 
                                                src={getUserPhotoURL(log) || `https://ui-avatars.com/api/?name=${encodeURIComponent((getUserDisplayName(log) || 'U').substring(0, 1))}&background=random`}
                                                alt="Avatar"
                                                className="w-6 h-6 rounded-full"
                                            />
                                            <span>{getUserDisplayName(log)}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                        <div className="flex items-start space-x-2">
                                            <i className={`${getActivityIcon(log.type)} mt-1`}></i>
                                            <span>{getActivityDescription(log)}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                        <button
                                            onClick={() => handleViewTaskDetailsById(log.taskId)}
                                            className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium focus:outline-none underline-offset-2 hover:underline"
                                            title={`Xem chi tiết: ${log.taskName}`}
                                        >
                                            {log.taskName}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                    Không tìm thấy nhật ký hoạt động phù hợp với bộ lọc.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {displayedLogs.length > logsPerPage && (
                <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button 
                            onClick={() => paginate(currentPage - 1)} 
                            disabled={currentPage === 1} 
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                        >
                            Trước
                        </button>
                        <button 
                            onClick={() => paginate(currentPage + 1)} 
                            disabled={currentPage === totalPages} 
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                        >
                            Sau
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700 dark:text-gray-400">
                                Hiển thị từ <span className="font-medium">{indexOfFirstLog + 1}</span> đến <span className="font-medium">{Math.min(indexOfLastLog, displayedLogs.length)}</span> trong tổng số <span className="font-medium">{displayedLogs.length}</span> nhật ký
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button 
                                    onClick={() => paginate(currentPage - 1)} 
                                    disabled={currentPage === 1} 
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                                >
                                    <span className="sr-only">Trước</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 010 1.414L10.586 10l3.293 3.293a1 1 01-1.414 1.414l-4-4a1 1 010-1.414l4-4a1 1 011.414-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300">
                                    Trang {currentPage} / {totalPages}
                                </span>
                                <button 
                                    onClick={() => paginate(currentPage + 1)} 
                                    disabled={currentPage === totalPages} 
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                                >
                                    <span className="sr-only">Sau</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 010-1.414L10.586 10l7.293 6.707a1 1 01-1.414 1.414l-4-4a1 1 010-1.414l4-4a1 1 011.414-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

//-----------------------------------------------------------------------

const Reports = () => {
    // --- State variables ---
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userData, setUserData] = useState(null);
    const [userTeams, setUserTeams] = useState([]);
    const [userTasks, setUserTasks] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [tasksByStatus, setTasksByStatus] = useState({ labels: [], data: [] });
    const [tasksByPriority, setTasksByPriority] = useState({ labels: [], data: [] });
    const [taskProgressByTeam, setTaskProgressByTeam] = useState({ labels: [], data: [] });
    const [taskCompletionTrend, setTaskCompletionTrend] = useState({ labels: [], data: [] });    const [selectedTimeRange, setSelectedTimeRange] = useState('month');
    const [selectedTeam, setSelectedTeam] = useState('all');
    const [selectedUser, setSelectedUser] = useState('all'); // Thêm state cho user filter
    // Thêm state cho bộ lọc tháng/năm
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [userListForDropdown, setUserListForDropdown] = useState([]); // Danh sách users cho dropdown
    const [userSearchTerm, setUserSearchTerm] = useState(''); // Search term cho user search
    const [showUserSuggestions, setShowUserSuggestions] = useState(false); // Show/hide suggestions
    const [filteredUserSuggestions, setFilteredUserSuggestions] = useState([]); // Filtered suggestions
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1); // Index của suggestion được highlight
    const [isSystemAdmin, setIsSystemAdmin] = useState(false);
    const [isTeamManager, setIsTeamManager] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [tasksPerPage] = useState(10);
    const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
    const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
    const [currentUserPermissions, setCurrentUserPermissions] = useState({
        isManagerOrAdmin: false,
        isAssignee: false
    });
    const [loadingAction, setLoadingAction] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [teamActivityLogs, setTeamActivityLogs] = useState([]); // NEW

    // --- State for File Handling in Modal ---
    const [taskFiles, setTaskFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const fileInputRef = useRef(null);

    // --- State for Comments in Modal ---
    const [newCommentText, setNewCommentText] = useState('');
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [commentError, setCommentError] = useState(null);
    const [replyTo, setReplyTo] = useState(null); // Track which comment is being replied to

    // --- Firebase Functions ---
    const functions = getFunctions();
    const getUserProfile = httpsCallable(functions, 'getUserProfile');
    const checkIsTeamManagerFunc = httpsCallable(functions, 'checkIsTeamManager');
    const updateTaskFunc = httpsCallable(functions, 'updateTask');

    // ... Other imports and code above remain unchanged

// --- Helper: Dynamic import with retry logic ---
const dynamicImportWithRetry = async (importFn, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await importFn();
        } catch (error) {
            if (error.name === 'ChunkLoadError' && i < maxRetries - 1) {
                console.warn(`Chunk load failed (attempt ${i + 1}/${maxRetries}), retrying...`, error);
                // Wait before retry
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            } else {
                throw error;
            }
        }
    }
};

// --- Export Excel & PDF ---
const handleExportExcel = async () => {
    if (filteredTasks.length === 0) {
        alert("Không có dữ liệu công việc để xuất.");
        return;
    }

    try {
        // Import ExcelJS with retry logic
        const ExcelJS = await dynamicImportWithRetry(() => import('exceljs'));
        const Excel = ExcelJS.default;
    
    const teamMap = userTeams.reduce((map, team) => {
        map[team.id] = team.name;
        return map;
    }, {});

    // Tạo workbook & worksheet mới
    const workbook = new Excel.Workbook();
    
    // Tạo worksheet với thiết lập khổ giấy ngang
    const worksheet = workbook.addWorksheet('Báo cáo công việc');
    
    // *** QUAN TRỌNG: Thiết lập khổ giấy LETTER NGANG ***
    worksheet.pageSetup = {
        paperSize: 9,                // Letter size (8.5" x 11")
        orientation: 'landscape',    // Định hướng ngang !!!
        horizontalCentered: true,    // Căn giữa nội dung theo chiều ngang
        fitToPage: true,             // Đảm bảo nội dung vừa trang
        fitToWidth: 1,               // Vừa vặn 1 trang theo chiều rộng
        fitToHeight: 0,              // Tự động tính số trang theo chiều cao
        scale: 95,                   // Giảm nhẹ tỷ lệ để đảm bảo vừa trang
        margins: {
            left: 0.2,               // Giảm lề trái (inch)
            right: 0.2,              // Giảm lề phải
            top: 0.5,                // Lề trên
            bottom: 0.5,             // Lề dưới
            header: 0.3,             // Lề header
            footer: 0.3              // Lề footer
        }
    };
    
    // Thiết lập footer CHỈ hiển thị số trang và ngày (BỎ tiêu đề khỏi headerFooter)
    worksheet.headerFooter = {
        oddFooter: '&CThông tin công ty | &D | Trang &P / &N'  // Footer với thông tin trang và ngày
    };
    
    // Xác định số cột dựa trên việc có lọc phòng ban hay không
    const numberOfColumns = selectedTeam === 'all' ? 13 : 12;
    const lastColumn = String.fromCharCode(64 + numberOfColumns); // Ví dụ: 13 -> M
    
    // Thêm một dòng trống ở đầu
    worksheet.addRow([]);
    
    // TIÊU ĐỀ CHÍNH - Chỉ giữ lại tiêu đề này, không dùng headerFooter
    const titleRow = worksheet.addRow(["BÁO CÁO CHI TIẾT CÔNG VIỆC"]);
    titleRow.height = 36; // Tăng chiều cao dòng tiêu đề
    worksheet.mergeCells(`A${titleRow.number}:${lastColumn}${titleRow.number}`);
    
    const titleCell = worksheet.getCell(`A${titleRow.number}`);
    titleCell.font = { name: 'Arial', size: 20, bold: true }; // Tăng cỡ chữ
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E2EFDA' }  // Màu nền xanh nhạt
    };
      // Thông tin phòng ban
    const teamFilterText = selectedTeam === 'all' 
        ? "Tất cả phòng ban" 
        : teamMap[selectedTeam] || `ID: ${selectedTeam}`;
    const teamRow = worksheet.addRow([`Phòng ban: ${teamFilterText}`]);
    teamRow.height = 24;
    worksheet.mergeCells(`A${teamRow.number}:${lastColumn}${teamRow.number}`);
    
    const teamCell = worksheet.getCell(`A${teamRow.number}`);
    teamCell.font = { name: 'Arial', size: 12, bold: true };
    teamCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Thông tin tháng/năm
    const monthYearRow = worksheet.addRow([`Báo cáo: Tháng ${selectedMonth}/${selectedYear}`]);
    monthYearRow.height = 24;
    worksheet.mergeCells(`A${monthYearRow.number}:${lastColumn}${monthYearRow.number}`);
    
    const monthYearCell = worksheet.getCell(`A${monthYearRow.number}`);
    monthYearCell.font = { name: 'Arial', size: 12, bold: true };
    monthYearCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Thông tin cá nhân (nếu có filter theo user)
    if (selectedUser !== 'all') {
        const selectedUserInfo = userListForDropdown.find(u => u.uid === selectedUser);
        const userFilterText = selectedUserInfo ? 
            `${selectedUserInfo.displayName} (${selectedUserInfo.teamName})` : 
            'Cá nhân đã chọn';
        const userRow = worksheet.addRow([`Cá nhân: ${userFilterText}`]);
        userRow.height = 24;
        worksheet.mergeCells(`A${userRow.number}:${lastColumn}${userRow.number}`);
        
        const userCell = worksheet.getCell(`A${userRow.number}`);
        userCell.font = { name: 'Arial', size: 12, bold: true };
        userCell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
    
    // Ngày xuất báo cáo
    const dateRow = worksheet.addRow([`Ngày xuất: ${formatDate(new Date(), true)}`]);
    worksheet.mergeCells(`A${dateRow.number}:${lastColumn}${dateRow.number}`);
    
    const dateCell = worksheet.getCell(`A${dateRow.number}`);
    dateCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: '666666' } };
    dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Thêm khoảng trống trước header
    worksheet.addRow([]);
    
    // Thiết lập headers bảng
    const headers = [
        "STT", "Tên công việc", "Mô tả", "Trạng thái", "Mức độ ưu tiên",
        "Người thực hiện", "Ngày bắt đầu", "Thời gian (ngày)", "Hạn chót",
        "Ngày hoàn thành/hủy", "Người phê duyệt", "Phòng ban", "Tiến độ (%)"
    ];
    
    // Xóa cột phòng ban nếu đã lọc theo phòng ban cụ thể
    let excelHeaders = [...headers];
    if (selectedTeam !== 'all') {
        excelHeaders.splice(11, 1); // Xóa cột "Phòng ban"
    }
    
    // Thêm và định dạng header bảng
    const headerRow = worksheet.addRow(excelHeaders);
    headerRow.height = 32; // Tăng chiều cao dòng header để hiển thị text nhiều dòng
    
    // Định dạng header - nền xanh chữ trắng
    headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '4472C4' }  // Xanh dương
        };
        cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } }; // Tăng font size
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });
    
    // Thêm dữ liệu từ filteredTasks
    filteredTasks.forEach((task, index) => {
        const dueDate = calculateDueDate(task.start_date, task.duration);
        let completionOrCancelDate = 'N/A';
        if (task.status === 'completed' && task.completedAt) {
            completionOrCancelDate = formatDateTime(task.completedAt);
        } else if (task.status === 'canceled' && task.canceledAt) {
            completionOrCancelDate = formatDateTime(task.canceledAt);
        } else if (task.status === 'rejected' && task.rejectedAt) {
            completionOrCancelDate = formatDateTime(task.rejectedAt);
        }
        
        const isOverdue = !['completed', 'canceled'].includes(task.status) && isTaskOverdue(task);
        const statusDisplay = isOverdue ? `${getStatusLabel(task.status)} (Quá hạn)` : getStatusLabel(task.status);
        const approverName = task.status === 'approved' ? (task.approverName || 'Không rõ') : '';
        
        let rowData = [
            index + 1,
            task.name || task.text || 'N/A',
            task.description || '',
            statusDisplay,
            getPriorityLabel(task.priority),
            getAssigneeNames(task.assignees),
            formatDate(task.start_date),
            task.duration || 'N/A',
            formatDate(dueDate),
            completionOrCancelDate,
            approverName
        ];
        
        // Thêm cột phòng ban nếu hiển thị tất cả phòng ban
        if (selectedTeam === 'all') {
            rowData.push(teamMap[task.teamId] || task.teamId || 'N/A');
        }
        
        // Thêm cột tiến độ
        rowData.push((task.progress || 0) + '%');
        
        // Thêm dòng dữ liệu
        const dataRow = worksheet.addRow(rowData);
        dataRow.height = 24; // Tăng chiều cao dòng dữ liệu
        
        // Định dạng dòng dữ liệu
        dataRow.eachCell((cell, colNumber) => {
            // Viền mỏng cho tất cả ô
            cell.border = {
                top: { style: 'thin', color: { argb: 'D3D3D3' } },
                left: { style: 'thin', color: { argb: 'D3D3D3' } },
                bottom: { style: 'thin', color: { argb: 'D3D3D3' } },
                right: { style: 'thin', color: { argb: 'D3D3D3' } }
            };
            
            // Font và căn lề cơ bản
            cell.font = { name: 'Arial', size: 10 };
            
            // Căn chỉnh theo loại dữ liệu
            if (colNumber === 1) { // STT
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else if (colNumber === 3) { // Mô tả
                cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            } else {
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            }
            
            // Màu nền cho dòng chẵn/lẻ để dễ đọc
            if (index % 2 === 1) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'F2F2F2' }  // Xám nhạt
                };
            }
            
            // Định dạng theo nội dung
            // Trạng thái
            if (colNumber === 4) {
                const status = String(cell.value || '');
                if (status.includes('Quá hạn')) {
                    cell.font = { ...cell.font, bold: true, color: { argb: 'FF0000' } }; // Đỏ
                } else if (status.includes('Hoàn thành')) {
                    cell.font = { ...cell.font, color: { argb: '008000' } }; // Xanh lá
                } else if (status.includes('Hủy')) {
                    cell.font = { ...cell.font, color: { argb: '808080' } }; // Xám
                }
            }
            
            // Mức độ ưu tiên
            if (colNumber === 5) {
                const priority = String(cell.value || '');
                if (priority.includes('Cao')) {
                    cell.font = { ...cell.font, bold: true, color: { argb: 'FF0000' } }; // Đỏ
                } else if (priority.includes('Trung bình')) {
                    cell.font = { ...cell.font, color: { argb: 'FFA500' } }; // Cam
                }
            }
            
            // Tiến độ
            if (colNumber === excelHeaders.length) { // Cột cuối cùng (tiến độ)
                const progressText = String(cell.value || '').replace('%', '');
                const progress = parseInt(progressText);
                if (progress >= 100) {
                    cell.font = { ...cell.font, bold: true, color: { argb: '008000' } }; // Xanh lá
                } else if (progress < 50) {
                    cell.font = { ...cell.font, color: { argb: 'FF9900' } }; // Cam
                }
            }
        });
    });
    
    // TĂNG CHIỀU RỘNG CỘT ĐỂ HIỂN THỊ ĐẦY ĐỦ TEXT
    // Thiết lập độ rộng cột cố định (thay vì tỷ lệ) để đảm bảo text hiển thị đầy đủ
    const columnWidths = [
        { col: 1, width: 5 },     // STT
        { col: 2, width: 25 },    // Tên công việc
        { col: 3, width: 30 },    // Mô tả
        { col: 4, width: 15 },    // Trạng thái
        { col: 5, width: 15 },    // Mức độ ưu tiên
        { col: 6, width: 20 },    // Người thực hiện
        { col: 7, width: 15 },    // Ngày bắt đầu
        { col: 8, width: 10 },    // Thời gian (ngày)
        { col: 9, width: 15 },    // Hạn chót
        { col: 10, width: 18 },   // Ngày hoàn thành/hủy
        { col: 11, width: 15 },   // Người phê duyệt
        { col: 12, width: 15 },   // Phòng ban
        { col: 13, width: 10 }    // Tiến độ
    ];
    
    // Áp dụng độ rộng cột
    columnWidths.forEach(col => {
        if (worksheet.getColumn(col.col)) {
            worksheet.getColumn(col.col).width = col.width;
        }
    });
    
    // Thêm vùng in được (print area)
    const lastRow = worksheet.rowCount;
    worksheet.pageSetup.printArea = `A1:${lastColumn}${lastRow}`;
    
    // Đặt tiêu đề lặp lại khi in nhiều trang (repeat rows)
    const headerRowNum = titleRow.number + 4; // Dòng header thực tế
    worksheet.pageSetup.printTitlesRow = `${headerRowNum}:${headerRowNum}`;
    
    // Xác định tên file
    const teamNameSlug = selectedTeam === 'all' ? 'tat_ca' : 
        (teamMap[selectedTeam] || selectedTeam || '').toString().toLowerCase().replace(/\s+/g, '_');
    const filename = `bao_cao_cong_viec_${teamNameSlug}_thang${selectedMonth}_${selectedYear}.xlsx`;
    
    // Kết xuất file
    await workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
    });
    } catch (error) {
        console.error('Export Excel failed:', error);
        if (error.name === 'ChunkLoadError') {
            alert('Lỗi tải dữ liệu. Vui lòng tải lại trang và thử lại (Ctrl+F5)');
        } else {
            alert('Lỗi xuất báo cáo Excel: ' + error.message);
        }
    }
};


    const handleExportPDF = () => {
        if (filteredTasks.length === 0) {
            alert("Không có dữ liệu công việc để xuất.");
            return;
        }
        // Tạo đối tượng PDF với định hướng ngang
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        // Thêm font chữ
        doc.addFont('https://cdn.jsdelivr.net/npm/source-sans-pro@3.6.0/TTF/SourceSansPro-Regular.ttf', 'SourceSans', 'normal');
        doc.addFont('https://cdn.jsdelivr.net/npm/source-sans-pro@3.6.0/TTF/SourceSansPro-Bold.ttf', 'SourceSans', 'bold');
        doc.setFont('SourceSans');
        const teamMap = userTeams.reduce((map, team) => {
            map[team.id] = team.name;
            return map;
        }, {});        const reportTitle = "BÁO CÁO CHI TIẾT CÔNG VIỆC";
        const teamFilterText = selectedTeam === 'all' ? "Tất cả phòng ban" : teamMap[selectedTeam] || `ID: ${selectedTeam}`;
        const monthYearText = `Tháng ${selectedMonth}/${selectedYear}`;
        const userFilterText = selectedUser !== 'all' ? 
            (userListForDropdown.find(u => u.uid === selectedUser)?.displayName || 'Cá nhân đã chọn') : 
            null;
        const generationDate = `Ngày xuất: ${formatDate(new Date(), true)}`;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        const startY = margin + 20;
        // try {
        //     const logoUrl = `${window.location.origin}/Logo.png`;
        //     doc.addImage(logoUrl, 'PNG', margin, margin, 20, 10);
        // } catch (error) {
        //     console.warn('Không thể tải logo:', error);
        // }
        doc.setFontSize(16);
        doc.setFont('SourceSans', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text(reportTitle, pageWidth / 2, margin + 8, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('SourceSans', 'normal');
        doc.setTextColor(52, 73, 94);
        doc.text(`Phòng ban: ${teamFilterText}`, margin + 25, margin + 8);
        doc.text(`Báo cáo: ${monthYearText}`, margin + 25, margin + 12);
        if (userFilterText) {
            doc.text(`Cá nhân: ${userFilterText}`, margin + 25, margin + 16);
        }        doc.text(generationDate, pageWidth - margin, margin + 8, { align: 'right' });
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.5);
        const lineY = userFilterText ? margin + 20 : margin + 16;
        doc.line(margin, lineY, pageWidth - margin, lineY);
        const tableHeaders = [
            "STT", "Tên CV", "Mô tả", "Trạng thái", "Ưu tiên", "Người TH", "Ngày BĐ", "Thời gian", "Hạn Chót", "Ngày HT/Hủy", "Người PD", "Phòng ban", "Tiến độ (%)"
        ];
        const teamColumnIndex = 11;
        if (selectedTeam !== 'all') {
            tableHeaders.splice(teamColumnIndex, 1);
        }
        const tableBody = filteredTasks.map((task, index) => {
            const dueDate = calculateDueDate(task.start_date, task.duration);
            let completionOrCancelDate = 'N/A';
            if (task.status === 'completed' && task.completedAt) {
                completionOrCancelDate = formatDateTime(task.completedAt);
            } else if (task.status === 'canceled' && task.canceledAt) {
                completionOrCancelDate = formatDateTime(task.canceledAt);
            } else if (task.status === 'rejected' && task.rejectedAt) {
                completionOrCancelDate = formatDateTime(task.rejectedAt);
            }
            const isOverdue = !['completed', 'canceled'].includes(task.status) && isTaskOverdue(task);
            const statusDisplay = isOverdue ? `${getStatusLabel(task.status)} (Quá hạn)` : getStatusLabel(task.status);
            const assigneesText = getAssigneeNames(task.assignees);
            const approverName = task.status === 'approved' ? (task.approverName || 'Không rõ') : '';
            let rowData = [
                index + 1,
                task.name || task.text || 'N/A',
                task.description || '',
                statusDisplay,
                getPriorityLabel(task.priority),
                assigneesText,
                formatDate(task.start_date),
                task.duration || 'N/A',
                formatDate(dueDate),
                completionOrCancelDate,
                approverName,
                selectedTeam === 'all' ? (teamMap[task.teamId] || task.teamId || 'N/A') : '',
                `${task.progress || 0}%`
            ];
            if (selectedTeam !== 'all') {
                rowData.splice(teamColumnIndex, 1);
            }
            return rowData;
        });
        const pageHeader = function(data) {
            if (data.pageNumber > 1) {
                doc.setFontSize(10);
                doc.setFont('SourceSans', 'normal');
                doc.setTextColor(52, 73, 94);
                doc.text(reportTitle, pageWidth / 2, margin, { align: 'center' });
                doc.setDrawColor(52, 152, 219);
                doc.setLineWidth(0.3);
                doc.line(margin, margin + 4, pageWidth - margin, margin + 4);
            }
        };
        const pageFooter = function(data) {
            doc.setFontSize(8);
            doc.setFont('SourceSans', 'normal');
            doc.setTextColor(127, 140, 141);
            const pageText = `Trang ${data.pageNumber}/${data.pageCount}`;
            doc.text(pageText, pageWidth - margin, pageHeight - margin/2);
            doc.text(`Xuất báo cáo: ${formatDateTime(new Date())}`, margin, pageHeight - margin/2);
            doc.setDrawColor(52, 152, 219);
            doc.setLineWidth(0.3);
            doc.line(margin, pageHeight - margin - 2, pageWidth - margin, pageHeight - margin - 2);
        };
        autoTable(doc, {
            styles: { 
                overflow: 'linebreak'
            },
            head: [tableHeaders],
            body: tableBody,
            startY: startY,
            theme: 'grid',
            headStyles: { 
                fillColor: [41, 128, 185], 
                textColor: 255, 
                fontStyle: 'bold', 
                halign: 'center', 
                font: 'SourceSans', 
                fontSize: 9,
                cellPadding: 2
            },
            bodyStyles: { 
                textColor: 50, 
                font: 'SourceSans', 
                fontSize: 8, 
                cellPadding: 2, 
                minCellHeight: 6,
                lineWidth: 0.1
            },
            alternateRowStyles: { 
                fillColor: [240, 248, 255] 
            },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 30 },
                2: { cellWidth: 45 },
                3: { cellWidth: 20, halign: 'center' },
                4: { cellWidth: 15, halign: 'center' },
                5: { cellWidth: 25 },
                6: { cellWidth: 17, halign: 'center' },
                7: { cellWidth: 15, halign: 'center' },
                8: { cellWidth: 17, halign: 'center' },
                9: { cellWidth: 22, halign: 'center' },
                10: { cellWidth: 20 },
                [teamColumnIndex]: { cellWidth: selectedTeam === 'all' ? 20 : 0 },
                [teamColumnIndex + (selectedTeam === 'all' ? 1 : 0)]: { cellWidth: 15, halign: 'right' }
            },
            //didParseCell: function (data) {
                // if (data.column.index === 2 || data.column.index === 5) {
                //     if (data.cell.raw && typeof data.cell.raw === 'string') {
                //         data.cell.text = doc.splitTextToSize(data.cell.raw, data.column.width - 2);
                //     }
                // }
                // if (data.section === 'body' && data.column.index === 3) {
                //     const statusText = data.cell.raw.toLowerCase();
                //     if (statusText.includes('quá hạn')) {
                //         data.cell.styles.textColor = [192, 57, 43];
                //     } else if (statusText.includes('hoàn thành')) {
                //         data.cell.styles.textColor = [39, 174, 96];
                //     } else if (statusText.includes('đã hủy') || statusText.includes('từ chối')) {
                //         data.cell.styles.textColor = [127, 140, 141];
                //     } else if (statusText.includes('chờ phê duyệt')) {
                //         data.cell.styles.textColor = [142, 68, 173];
                //     } else if (statusText.includes('đang thực hiện')) {
                //         data.cell.styles.textColor = [41, 128, 185];
                //     }
                // }
                didParseCell: (data) => {
                    // chỉ áp dụng cho phần body
                    if (data.section === 'body' && (data.column.dataKey === 'description' || data.column.dataKey === 'assignees')) {
                      data.cell.text = doc.splitTextToSize(String(data.cell.raw), data.cell.width);
                    }
            },
            willDrawCell: function(data) {
                if (data.section === 'body') {
                    const rowIndex = data.row.index;
                    const task = filteredTasks[rowIndex];
                    if (task && !['completed', 'canceled'].includes(task.status) && isTaskOverdue(task)) {
                        doc.setFillColor(255, 236, 236);
                        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                    }
                }
            },
            margin: { top: margin, left: margin, right: margin, bottom: margin + 5 },
            didDrawPage: function(data) {
                pageHeader(data);
                pageFooter(data);
            }
        });
        const lastY = doc.lastAutoTable.finalY || startY;
        if (lastY + 30 < pageHeight - margin) {
            doc.setFontSize(10);
            doc.setFont('SourceSans', 'bold');
            doc.setTextColor(44, 62, 80);
            doc.text('Tóm tắt báo cáo:', margin, lastY + 15);
            doc.setFont('SourceSans', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(52, 73, 94);
            const completedCount = filteredTasks.filter(t => t.status === 'completed').length;
            const inProgressCount = filteredTasks.filter(t => ['inProgress', 'in-progress'].includes(t.status)).length;
            const overdueCount = filteredTasks.filter(t => !['completed', 'canceled'].includes(t.status) && isTaskOverdue(t)).length;
            doc.text(`- Tổng số công việc: ${filteredTasks.length}`, margin + 5, lastY + 22);
            doc.text(`- Đã hoàn thành: ${completedCount} (${filteredTasks.length > 0 ? Math.round((completedCount / filteredTasks.length) * 100) : 0}%)`, margin + 5, lastY + 28);
            doc.text(`- Đang thực hiện: ${inProgressCount}`, margin + 70, lastY + 22);
            doc.text(`- Quá hạn: ${overdueCount}`, margin + 70, lastY + 28);
        }
        const teamNameSlug = selectedTeam === 'all' ? 'tat_ca' : (teamMap[selectedTeam] || selectedTeam).toLowerCase().replace(/\s+/g, '_');
        doc.save(`bao_cao_cong_viec_${teamNameSlug}_thang${selectedMonth}_${selectedYear}.pdf`);
    };

    // --- Export DOCX (NEW) ---
    const handleExportDOCX = async () => {
        if (filteredTasks.length === 0) {
            alert("Không có dữ liệu công việc để xuất.");
            return;
        }

        const teamMap = userTeams.reduce((map, team) => {
            map[team.id] = team.name;
            return map;
        }, {});

        const reportTitle = "BÁO CÁO CHI TIẾT CÔNG VIỆC";
        const teamFilterText = selectedTeam === 'all' 
            ? "Tất cả phòng ban" 
            : teamMap[selectedTeam] || `ID: ${selectedTeam}`;
        const monthYearText = `Tháng ${selectedMonth}/${selectedYear}`;
        const userFilterText = selectedUser !== 'all' ? 
            (userListForDropdown.find(u => u.uid === selectedUser)?.displayName || 'Cá nhân đã chọn') : 
            null;
        const generationDate = `Ngày xuất: ${formatDate(new Date(), true)}`;

        // Định nghĩa độ rộng cột (đơn vị: DXA - 1440 DXA = 1 inch)
        // Tổng width trang ngang A4: ~14400 DXA (10 inches)
        const columnWidths = [
            600,   // STT - 600
            1500,  // Tên CV - 1500
            2000,  // Mô tả - 2000
            1200,  // Trạng thái - 1200
            800,   // Ưu tiên - 800
            1200,  // Người TH - 1200
            900,   // Ngày BĐ - 900
            700,   // Thời gian - 700
            900,   // Hạn Chót - 900
            1100,  // Ngày HT/Hủy - 1100
            1000,  // Người PD - 1000
            700    // Tiến độ - 700
        ];

        // Tạo header rows cho bảng
        const tableHeaders = [
            "STT", "Tên Công Việc", "Mô Tả", "Trạng Thái", "Ưu Tiên", 
            "Người Thực Hiện", "Ngày BĐ", "TG (ngày)", "Hạn Chót", 
            "Ngày HT/Hủy", "Người Phê Duyệt", "Tiến Độ"
        ];
        
        // Thêm cột "Phòng ban" nếu đang xem tất cả phòng ban
        if (selectedTeam === 'all') {
            tableHeaders.splice(11, 0, "Phòng Ban");
            columnWidths.splice(11, 0, 1000);
        }

        const tableHeaderCells = tableHeaders.map((header, index) => 
            new TableCell({
                children: [new Paragraph({
                    children: [
                        new TextRun({
                            text: header,
                            bold: true,
                            size: 18, // 9pt font (18 half-points)
                            color: "FFFFFF", // Chữ trắng để tương phản với nền xanh navy
                            font: "Arial"
                        })
                    ],
                    alignment: AlignmentType.CENTER
                })],
                shading: {
                    fill: "1F4E78", // Màu xanh navy chuyên nghiệp
                    color: "FFFFFF"
                },
                width: { size: columnWidths[index], type: WidthType.DXA },
                margins: {
                    top: 100,
                    bottom: 100,
                    left: 100,
                    right: 100
                },
                verticalAlign: AlignmentType.CENTER
            })
        );

        // Tạo data rows
        const tableDataRows = filteredTasks.map((task, index) => {
            const dueDate = calculateDueDate(task.start_date, task.duration);
            let completionOrCancelDate = 'N/A';
            if (task.status === 'completed' && task.completedAt) {
                completionOrCancelDate = formatDateTime(task.completedAt);
            } else if (task.status === 'canceled' && task.canceledAt) {
                completionOrCancelDate = formatDateTime(task.canceledAt);
            } else if (task.status === 'rejected' && task.rejectedAt) {
                completionOrCancelDate = formatDateTime(task.rejectedAt);
            }

            const isOverdue = !['completed', 'canceled'].includes(task.status) && isTaskOverdue(task);
            const statusDisplay = isOverdue ? `${getStatusLabel(task.status)} (Quá hạn)` : getStatusLabel(task.status);
            const assigneesText = getAssigneeNames(task.assignees);
            const approverName = task.status === 'approved' ? (task.approverName || 'Không rõ') : '';
            
            // Giới hạn độ dài mô tả
            const description = task.description || '';
            const shortDescription = description.length > 150 ? description.substring(0, 147) + '...' : description;

            let rowData = [
                String(index + 1),
                task.name || task.text || 'N/A',
                shortDescription,
                statusDisplay,
                getPriorityLabel(task.priority),
                assigneesText,
                formatDate(task.start_date),
                task.duration || 'N/A',
                formatDate(dueDate),
                completionOrCancelDate,
                approverName,
                `${task.progress || 0}%`
            ];

            if (selectedTeam === 'all') {
                rowData.splice(11, 0, teamMap[task.teamId] || task.teamId || 'N/A');
            }

            // Màu nền xen kẽ cho dễ đọc
            const isEvenRow = index % 2 === 0;
            const rowShading = isEvenRow ? "F9F9F9" : "FFFFFF";

            return new TableRow({
                children: rowData.map((cellData, cellIndex) => {
                    // Xác định alignment
                    let alignment = AlignmentType.LEFT;
                    if (cellIndex === 0 || cellIndex === tableHeaders.length - 1) { // STT và Tiến độ
                        alignment = AlignmentType.CENTER;
                    }

                    // Màu nền cho cell quá hạn
                    let cellShading = rowShading;
                    if (isOverdue && cellIndex === 3) { // Cột trạng thái
                        cellShading = "FFE5E5";
                    }

                    return new TableCell({
                        children: [new Paragraph({
                            children: [
                                new TextRun({
                                    text: cellData,
                                    size: 18, // 9pt font
                                    font: "Arial"
                                })
                            ],
                            alignment: alignment
                        })],
                        shading: {
                            fill: cellShading,
                            color: "000000"
                        },
                        width: { size: columnWidths[cellIndex], type: WidthType.DXA },
                        margins: {
                            top: 80,
                            bottom: 80,
                            left: 80,
                            right: 80
                        },
                        verticalAlign: AlignmentType.CENTER
                    });
                })
            });
        });

        // Tạo document với orientation ngang (landscape)
        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        // A4 Landscape: width=16838 (11.69"), height=11906 (8.27")
                        width: 16838,
                        height: 11906,
                        margin: {
                            top: 720,    // 0.5 inch
                            right: 720,  // 0.5 inch
                            bottom: 720, // 0.5 inch
                            left: 720    // 0.5 inch
                        }
                    }
                },
                children: [
                    // Tiêu đề chính
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: reportTitle,
                                bold: true,
                                size: 32, // 16pt
                                color: "1F4E78",
                                font: "Arial"
                            })
                        ],
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 150 }
                    }),
                    // Thông tin phòng ban
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "Phòng ban: ",
                                bold: true,
                                size: 22, // 11pt
                                font: "Arial"
                            }),
                            new TextRun({
                                text: teamFilterText,
                                size: 22,
                                font: "Arial"
                            })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 80 }
                    }),
                    // Thông tin tháng/năm
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "Báo cáo: ",
                                bold: true,
                                size: 22,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: monthYearText,
                                size: 22,
                                font: "Arial",
                                color: "C00000"
                            })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 80 }
                    }),
                    // Thông tin cá nhân (nếu có)
                    ...(userFilterText ? [new Paragraph({
                        children: [
                            new TextRun({
                                text: "Cá nhân: ",
                                bold: true,
                                size: 22,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: userFilterText,
                                size: 22,
                                font: "Arial"
                            })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 80 }
                    })] : []),
                    // Ngày xuất
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: generationDate,
                                size: 18,
                                font: "Arial",
                                italics: true,
                                color: "666666"
                            })
                        ],
                        alignment: AlignmentType.RIGHT,
                        spacing: { after: 300 }
                    }),
                    // Bảng dữ liệu
                    new Table({
                        rows: [
                            new TableRow({
                                children: tableHeaderCells,
                                tableHeader: true,
                                height: { value: 600, rule: "atLeast" }
                            }),
                            ...tableDataRows
                        ],
                        width: {
                            size: 100,
                            type: WidthType.PERCENTAGE
                        },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 6, color: "1F4E78" },
                            bottom: { style: BorderStyle.SINGLE, size: 6, color: "1F4E78" },
                            left: { style: BorderStyle.SINGLE, size: 6, color: "1F4E78" },
                            right: { style: BorderStyle.SINGLE, size: 6, color: "1F4E78" },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
                            insideVertical: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" }
                        },
                        layout: "fixed"
                    }),
                    // Tóm tắt báo cáo
                    new Paragraph({
                        text: "",
                        spacing: { before: 300, after: 150 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "TÓM TẮT BÁO CÁO",
                                bold: true,
                                size: 24, // 12pt
                                color: "1F4E78",
                                font: "Arial"
                            })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 150 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "• Tổng số công việc: ",
                                bold: true,
                                size: 20,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: `${filteredTasks.length}`,
                                size: 20,
                                font: "Arial",
                                color: "1F4E78",
                                bold: true
                            })
                        ],
                        spacing: { after: 100 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "• Đã hoàn thành: ",
                                bold: true,
                                size: 20,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: `${filteredTasks.filter(t => t.status === 'completed').length} `,
                                size: 20,
                                font: "Arial",
                                color: "00B050",
                                bold: true
                            }),
                            new TextRun({
                                text: `(${filteredTasks.length > 0 ? Math.round((filteredTasks.filter(t => t.status === 'completed').length / filteredTasks.length) * 100) : 0}%)`,
                                size: 20,
                                font: "Arial",
                                color: "666666"
                            })
                        ],
                        spacing: { after: 100 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "• Đang thực hiện: ",
                                bold: true,
                                size: 20,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: `${filteredTasks.filter(t => ['inProgress', 'in-progress'].includes(t.status)).length}`,
                                size: 20,
                                font: "Arial",
                                color: "FFA500",
                                bold: true
                            })
                        ],
                        spacing: { after: 100 }
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "• Quá hạn: ",
                                bold: true,
                                size: 20,
                                font: "Arial"
                            }),
                            new TextRun({
                                text: `${filteredTasks.filter(t => !['completed', 'canceled'].includes(t.status) && isTaskOverdue(t)).length}`,
                                size: 20,
                                font: "Arial",
                                color: "C00000",
                                bold: true
                            })
                        ],
                        spacing: { after: 100 }
                    })
                ]
            }]
        });

        // Xuất file
        const blob = await Packer.toBlob(doc);
        const teamNameSlug = selectedTeam === 'all' ? 'tat_ca' : (teamMap[selectedTeam] || selectedTeam).toLowerCase().replace(/\s+/g, '_');
        const fileName = `bao_cao_cong_viec_${teamNameSlug}_thang${selectedMonth}_${selectedYear}.docx`;
        saveAs(blob, fileName);
    };

    // --- Data Fetching & Processing ---
    useEffect(() => {
        const fetchUserData = async () => {
            if (!auth.currentUser) {
                setLoading(false);
                setError("Vui lòng đăng nhập để xem báo cáo.");
                return;
            }
            try {
                setLoading(true);
                setError(null);
                const userId = auth.currentUser.uid;
                // Lấy profile để lấy email
                const result = await getUserProfile({ userId });

                if (result.data.error) {
                    throw new Error(result.data.error);
                }                const { user, teams, tasks, role } = result.data;
                setUserData(user);                // Kiểm tra quyền admin và team manager
                const adminEmails = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com', 'xofosy@thetechnext.net'];
                const systemAdminCheck = adminEmails.includes(user.email);
                setIsSystemAdmin(systemAdminCheck);

                let usersForDropdown = [];
                let managerOfAnyTeam = false;

                // Kiểm tra nếu là admin đặc biệt
                if (user && systemAdminCheck) {
                    try {
                        // Lấy tất cả team từ collection 'teams'
                        const teamsSnapshot = await getDocs(collection(db, 'teams'));
                        
                        // Đối với admin, lấy tất cả team không phải private (hoặc không có thuộc tính private)
                        const allTeams = teamsSnapshot.docs.map(docSnap => ({ 
                            id: docSnap.id, 
                            ...docSnap.data()
                        }));

                        // Lọc team theo logic sau:
                        // 1. Team không được đánh dấu là private (private === false hoặc không có thuộc tính private)
                        // 2. Hoặc team có members hoặc membersData (đây là định dạng cũ)
                        const visibleTeams = allTeams.filter(team => 
                            // Team không được đánh dấu là private
                            (team.private === undefined || team.private === false) && 
                            // Đảm bảo team này có members hoặc membersData (loại bỏ các team rỗng)
                            (Array.isArray(team.members) || Array.isArray(team.membersData) || 
                             (typeof team.members === 'object' && team.members !== null)) 
                        );

                        console.log("Admin view - Số team đã lọc:", visibleTeams.length);
                          setUserTeams(visibleTeams);
                        
                        // Lấy tất cả task thuộc các team này
                        const teamIds = visibleTeams.map(t => t.id);
                        
                        // Lấy task từ danh sách đã có và lọc theo team ID
                        const allTasks = (tasks || []).filter(task => teamIds.includes(task.teamId));
                        setUserTasks(allTasks);
                        setUserRole(role || null);
                        
                        // Thu thập danh sách users từ các team và tasks (cho admin)
                        const userSet = new Set();
                        visibleTeams.forEach(team => {
                            // Từ members
                            if (Array.isArray(team.members)) {
                                team.members.forEach(member => {
                                    if (member.uid && member.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: member.uid,
                                            displayName: member.displayName,
                                            email: member.email || '',
                                            teamName: team.name
                                        }));
                                    }
                                });
                            }
                            // Từ membersData (format cũ)
                            if (Array.isArray(team.membersData)) {
                                team.membersData.forEach(member => {
                                    if (member.uid && member.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: member.uid,
                                            displayName: member.displayName,
                                            email: member.email || '',
                                            teamName: team.name
                                        }));
                                    }
                                });
                            }
                        });
                        
                        // Thu thập từ assignees trong tasks
                        allTasks.forEach(task => {
                            if (Array.isArray(task.assignees)) {
                                task.assignees.forEach(assignee => {
                                    if (assignee.uid && assignee.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: assignee.uid,
                                            displayName: assignee.displayName,
                                            email: assignee.email || '',
                                            teamName: userTeams.find(t => t.id === task.teamId)?.name || 'N/A'
                                        }));
                                    }
                                });
                            }
                        });
                        
                        usersForDropdown = Array.from(userSet).map(userStr => JSON.parse(userStr));
                        managerOfAnyTeam = true; // Admin có quyền xem tất cả
                        
                    } catch (error) {
                        console.error('Lỗi khi lấy dữ liệu team cho admin:', error);
                        // Fallback về danh sách teams từ getUserProfile nếu có lỗi
                        const publicTeams = (teams || []).filter(t => !t.private);
                        setUserTeams(publicTeams);
                        const publicTeamIds = publicTeams.map(t => t.id);
                        const publicTasks = (tasks || []).filter(task => publicTeamIds.includes(task.teamId));
                        setUserTasks(publicTasks);
                        setUserRole(role || null);
                    }                } else {
                    // Người dùng thường: chỉ lấy các phòng ban công khai mà user là thành viên
                    const publicTeams = (teams || []).filter(t => t.private === undefined || !t.private);
                    setUserTeams(publicTeams);
                    const publicTeamIds = publicTeams.map(t => t.id);
                    const publicTasks = (tasks || []).filter(task => publicTeamIds.includes(task.teamId));
                    setUserTasks(publicTasks);
                    setUserRole(role || null);
                    
                    // Kiểm tra xem user có phải là manager của team nào không
                    publicTeams.forEach(team => {
                        if (team.managerId === user.uid || 
                            (Array.isArray(team.managers) && team.managers.some(m => m.uid === user.uid))) {
                            managerOfAnyTeam = true;
                        }
                    });
                    
                    // Nếu là manager, thu thập users từ các team mà họ quản lý
                    if (managerOfAnyTeam) {
                        const userSet = new Set();
                        const managedTeams = publicTeams.filter(team => 
                            team.managerId === user.uid || 
                            (Array.isArray(team.managers) && team.managers.some(m => m.uid === user.uid))
                        );
                        
                        managedTeams.forEach(team => {
                            // Từ members
                            if (Array.isArray(team.members)) {
                                team.members.forEach(member => {
                                    if (member.uid && member.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: member.uid,
                                            displayName: member.displayName,
                                            email: member.email || '',
                                            teamName: team.name
                                        }));
                                    }
                                });
                            }
                            // Từ membersData (format cũ)
                            if (Array.isArray(team.membersData)) {
                                team.membersData.forEach(member => {
                                    if (member.uid && member.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: member.uid,
                                            displayName: member.displayName,
                                            email: member.email || '',
                                            teamName: team.name
                                        }));
                                    }
                                });
                            }
                        });
                        
                        // Thu thập từ assignees trong tasks của teams được quản lý
                        const managedTeamIds = managedTeams.map(t => t.id);
                        const managedTasks = publicTasks.filter(task => managedTeamIds.includes(task.teamId));
                        managedTasks.forEach(task => {
                            if (Array.isArray(task.assignees)) {
                                task.assignees.forEach(assignee => {
                                    if (assignee.uid && assignee.displayName) {
                                        userSet.add(JSON.stringify({
                                            uid: assignee.uid,
                                            displayName: assignee.displayName,
                                            email: assignee.email || '',
                                            teamName: publicTeams.find(t => t.id === task.teamId)?.name || 'N/A'
                                        }));
                                    }
                                });
                            }
                        });
                        
                        usersForDropdown = Array.from(userSet).map(userStr => JSON.parse(userStr));
                    }
                }
                
                // Cập nhật state cho dropdown users và quyền
                setUserListForDropdown(usersForDropdown.sort((a, b) => a.displayName.localeCompare(b.displayName)));
                setIsTeamManager(managerOfAnyTeam);
            } catch (error) {
                console.error('Lỗi khi lấy dữ liệu báo cáo:', error);
                setError(`Không thể tải dữ liệu báo cáo: ${error.message}`);
                setUserData(null);
                setUserTeams([]);
                setUserTasks([]);
                setUserRole(null);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [refreshTrigger]);

    useEffect(() => {
        const fetchTeamLogs = async () => {
            if (!userTeams || userTeams.length === 0) {
                setTeamActivityLogs([]);
                return;
            }
            try {
                const logs = [];
                for (const team of userTeams) {
                    const teamDoc = await getDoc(doc(db, 'teams', team.id));
                    if (teamDoc.exists() && Array.isArray(teamDoc.data().activityLogs)) {
                        teamDoc.data().activityLogs.forEach(log => {
                            logs.push({ ...log, teamId: team.id, teamName: team.name });
                        });
                    }
                }
                setTeamActivityLogs(logs);
            } catch (e) {
                setTeamActivityLogs([]);
            }
        };        fetchTeamLogs();
    }, [userTeams]);    // Reset user filter nếu không có quyền hoặc user không còn trong danh sách
    useEffect(() => {
        if (!isSystemAdmin && !isTeamManager) {
            setSelectedUser('all');
            setUserSearchTerm('');
        } else if (selectedUser !== 'all' && !userListForDropdown.some(u => u.uid === selectedUser)) {
            setSelectedUser('all');
            setUserSearchTerm('');
        } else if (selectedUser === 'all') {
            setUserSearchTerm('');
        }
    }, [isSystemAdmin, isTeamManager, userListForDropdown, selectedUser]);    // Filter user suggestions based on search term
    useEffect(() => {
        if (!userSearchTerm.trim()) {
            setFilteredUserSuggestions([]);
            setShowUserSuggestions(false);
            setSelectedSuggestionIndex(-1);
        } else {
            const filtered = userListForDropdown.filter(user =>
                user.displayName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                user.teamName.toLowerCase().includes(userSearchTerm.toLowerCase())
            );
            setFilteredUserSuggestions(filtered.slice(0, 10)); // Chỉ hiển thị 10 kết quả đầu tiên
            setShowUserSuggestions(filtered.length > 0);
            setSelectedSuggestionIndex(-1);
        }
    }, [userSearchTerm, userListForDropdown]);

    // Handle clicks outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            const searchContainer = document.getElementById('user-search-container');
            if (searchContainer && !searchContainer.contains(event.target)) {
                setShowUserSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filteredTasks = useMemo(() => {
        if (!userTasks) return [];
        
        let filtered = userTasks;
        
        // Filter theo team
        if (selectedTeam !== 'all') {
            filtered = filtered.filter(task => task.teamId === selectedTeam);
        }
        
        // Filter theo user
        if (selectedUser !== 'all') {
            filtered = filtered.filter(task => {
                if (!Array.isArray(task.assignees)) return false;
                return task.assignees.some(assignee => assignee.uid === selectedUser);
            });
        }
        
        // Filter theo tháng/năm (BUG FIX: Bao gồm cả công việc đang làm từ tháng trước)
        if (selectedMonth && selectedYear) {
            filtered = filtered.filter(task => {
                const startDate = getDateFromTimestamp(task.start_date);
                if (!startDate) return false;
                
                const taskStartMonth = startDate.getMonth() + 1; // 1-12
                const taskStartYear = startDate.getFullYear();
                
                // Tính ngày kết thúc dự kiến
                const dueDate = calculateDueDate(task.start_date, task.duration);
                
                // Điều kiện 1: Công việc bắt đầu trong tháng được chọn
                const startsInSelectedMonth = (taskStartMonth === selectedMonth && taskStartYear === selectedYear);
                
                // Điều kiện 2: Công việc bắt đầu từ tháng trước nhưng vẫn đang thực hiện (chưa hoàn thành/hủy)
                // và hạn chót vào hoặc sau tháng được chọn
                let ongoingFromPreviousMonth = false;
                if (dueDate) {
                    const selectedMonthStart = new Date(selectedYear, selectedMonth - 1, 1); // Ngày đầu tháng được chọn
                    const taskNotCompleted = !['completed', 'canceled', 'rejected'].includes(task.status);
                    const startedBefore = startDate < selectedMonthStart;
                    const dueAfterOrIn = dueDate >= selectedMonthStart;
                    
                    ongoingFromPreviousMonth = taskNotCompleted && startedBefore && dueAfterOrIn;
                }
                
                return startsInSelectedMonth || ongoingFromPreviousMonth;
            });
        }
        
        return filtered;
    }, [userTasks, selectedTeam, selectedUser, selectedMonth, selectedYear]);

    useEffect(() => {
        if (!loading) {
            processTasksByStatus(filteredTasks);
            processTasksByPriority(filteredTasks);
            processTaskProgressByTeam(userTasks, userTeams);
            processTaskCompletionTrend(userTasks);
            if (filteredTasks.length > 0 && currentPage > Math.ceil(filteredTasks.length / tasksPerPage)) {
                setCurrentPage(1);
            } else if (currentPage !== 1 && filteredTasks.length === 0) {
                setCurrentPage(1);
            }
        }
    }, [filteredTasks, selectedTeam, userTasks, userTeams, selectedTimeRange, loading, currentPage, tasksPerPage]);

    const processTasksByStatus = (tasks) => {
        const statusCounts = {
            'not-started': 0, 'in-progress': 0, 'pendingApproval': 0, 'approved': 0,
            'completed': 0, 'overdue': 0, 'canceled': 0, 'rejected': 0
        };
        tasks.forEach(task => {
            if (task.status === 'canceled') statusCounts.canceled++;
            else if (task.status === 'rejected') statusCounts.rejected++;
            else if (isTaskOverdue(task)) statusCounts.overdue++;
            else if (task.status === 'completed') statusCounts.completed++;
            else if (task.status === 'inProgress' || task.status === 'in-progress') statusCounts['in-progress']++;
            else if (task.status === 'pendingApproval') statusCounts.pendingApproval++;
            else if (task.status === 'approved') statusCounts.approved++;
            else if (task.status === 'notStarted' || task.status === 'not-started' || !task.status) statusCounts['not-started']++;
        });
        const labels = ['Chưa bắt đầu', 'Đang thực hiện', 'Chờ phê duyệt', 'Đã phê duyệt', 'Đã hoàn thành', 'Quá hạn', 'Đã hủy', 'Đã từ chối'];
        const data = [
            statusCounts['not-started'], statusCounts['in-progress'], statusCounts.pendingApproval, statusCounts.approved,
            statusCounts.completed, statusCounts.overdue, statusCounts.canceled, statusCounts.rejected
        ];
        setTasksByStatus({ labels, data });
    };

    const processTasksByPriority = (tasks) => {
        const priorityCounts = { 'low': 0, 'medium': 0, 'high': 0 };
        tasks.forEach(task => {
            const priority = task.priority || 'medium';
            if (priorityCounts.hasOwnProperty(priority)) priorityCounts[priority]++;
            else priorityCounts['medium']++;
        });
        const labels = ['Thấp', 'Trung bình', 'Cao'];
        const data = [priorityCounts.low, priorityCounts.medium, priorityCounts.high];
        setTasksByPriority({ labels, data });
    };

    const processTaskProgressByTeam = (allTasks, teams) => {
        if (!teams || teams.length === 0 || !allTasks || allTasks.length === 0) {
            setTaskProgressByTeam({ labels: [], data: [] });
            return;
        }
        const teamProgressSum = {};
        const teamTaskCounts = {};
        teams.forEach(team => {
            teamProgressSum[team.id] = 0;
            teamTaskCounts[team.id] = 0;
        });
        allTasks.forEach(task => {
            if (task.teamId && teamProgressSum.hasOwnProperty(task.teamId) && task.status !== 'canceled') {
                teamProgressSum[task.teamId] += task.progress || 0;
                teamTaskCounts[task.teamId]++;
            }
        });
        const teamsWithActiveTasks = teams.filter(team => teamTaskCounts[team.id] > 0);
        const labels = teamsWithActiveTasks.map(team => team.name);
        const data = teamsWithActiveTasks.map(team => teamTaskCounts[team.id] > 0 ? Math.round(teamProgressSum[team.id] / teamTaskCounts[team.id]) : 0);
        setTaskProgressByTeam({ labels, data });
    };

    const processTaskCompletionTrend = (allTasks) => {
        if (!allTasks || allTasks.length === 0) {
            setTaskCompletionTrend({ labels: [], data: [] });
            return;
        }
        const now = new Date();
        let startDate = new Date();
        let labels = [];
        let numIntervals = 0;
        let intervalType = 'day';
        if (selectedTimeRange === 'week') {
            numIntervals = 7;
            startDate.setDate(now.getDate() - (numIntervals - 1));
            labels = Array.from({ length: numIntervals }, (_, i) => {
                const date = new Date(startDate);
                date.setDate(startDate.getDate() + i);
                return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
            });
            intervalType = 'day';
        } else if (selectedTimeRange === 'month') {
            numIntervals = 4;
            startDate.setDate(now.getDate() - 28);
            labels = Array.from({ length: numIntervals }, (_, i) => `Tuần ${i + 1}`);
            intervalType = 'week';
        } else if (selectedTimeRange === 'year') {
            numIntervals = 12;
            startDate = new Date(now.getFullYear(), now.getMonth() - (numIntervals - 1), 1);
            labels = Array.from({ length: numIntervals }, (_, i) => {
                const date = new Date(startDate);
                date.setMonth(startDate.getMonth() + i);
                return date.toLocaleDateString('vi-VN', { month: 'short', year: 'numeric' });
            });
            intervalType = 'month';
        }
        startDate.setHours(0, 0, 0, 0);
        const completedTasks = allTasks.filter(task => task.status === 'completed' && task.completedAt && getDateFromTimestamp(task.completedAt) >= startDate && getDateFromTimestamp(task.completedAt) <= now);
        const data = Array(numIntervals).fill(0);
        completedTasks.forEach(task => {
            const completedDate = getDateFromTimestamp(task.completedAt);
            if (!completedDate) return;
            let index = -1;
            const diffTime = completedDate.getTime() - startDate.getTime();
            if (intervalType === 'day') {
                index = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            } else if (intervalType === 'week') {
                index = Math.min(numIntervals - 1, Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7)));
            } else if (intervalType === 'month') {
                index = (completedDate.getFullYear() - startDate.getFullYear()) * 12 + completedDate.getMonth() - startDate.getMonth();
            }
            if (index >= 0 && index < numIntervals) {
                data[index]++;
            }
        });
        setTaskCompletionTrend({ labels, data });
    };    // --- Event Handlers ---
    const handleTimeRangeChange = (range) => setSelectedTimeRange(range);    const handleTeamFilterChange = (e) => {
        setSelectedTeam(e.target.value);
        setSelectedUser('all'); // Reset user filter khi đổi team
        setUserSearchTerm(''); // Reset search term
        setShowUserSuggestions(false);
        setCurrentPage(1);
    };
      const handleUserSearchChange = (e) => {
        setUserSearchTerm(e.target.value);
        setSelectedSuggestionIndex(-1);
    };
    
    const handleUserSearchKeyDown = (e) => {
        if (!showUserSuggestions || filteredUserSuggestions.length === 0) return;
        
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedSuggestionIndex(prev => 
                    prev < filteredUserSuggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedSuggestionIndex(prev => 
                    prev > 0 ? prev - 1 : filteredUserSuggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedSuggestionIndex >= 0) {
                    handleUserSelect(filteredUserSuggestions[selectedSuggestionIndex]);
                }
                break;
            case 'Escape':
                setShowUserSuggestions(false);
                setSelectedSuggestionIndex(-1);
                break;
        }
    };
    
    const handleUserSelect = (user) => {
        setSelectedUser(user.uid);
        setUserSearchTerm(user.displayName);
        setShowUserSuggestions(false);
        setSelectedSuggestionIndex(-1);
        setCurrentPage(1);
    };
    
    const handleClearUserSearch = () => {
        setSelectedUser('all');
        setUserSearchTerm('');
        setShowUserSuggestions(false);
        setSelectedSuggestionIndex(-1);
        setCurrentPage(1);
    };
    
    const handleUserFilterChange = (e) => {
        setSelectedUser(e.target.value);
        setCurrentPage(1);
    };
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleViewTaskDetails = async (task) => {
        if (!task || !task.teamId) {
            console.error("Task data or teamId is missing for permission check.");
            setError("Không thể kiểm tra quyền hạn cho công việc này.");
            return;
        }
        setSelectedTaskForDetail(task);
        setShowTaskDetailModal(true);
        setActionError(null);
        setCurrentUserPermissions({ isManagerOrAdmin: false, isAssignee: false });
        setTaskFiles(task.files || []);
        setUploadError(null);
        setNewCommentText('');
        setCommentError(null);

        if (!auth.currentUser) return;

        const isAssignee = task.assignees?.some(a => a.uid === auth.currentUser.uid) || false;

        try {
            setLoadingAction(true);
            const result = await checkIsTeamManagerFunc({ teamId: task.teamId });
            setCurrentUserPermissions({
                isManagerOrAdmin: result.data.isManager || false,
                isAssignee: isAssignee
            });
        } catch (error) {
            console.error("Lỗi khi kiểm tra quyền quản lý:", error);
            setActionError("Không thể kiểm tra quyền của bạn cho công việc này.");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleViewTaskDetailsById = (taskId) => {
        const task = filteredTasks.find(t => t.id === taskId) || teamActivityLogs.find(log => log.taskId === taskId)?.task || null;
        if (task) {
            handleViewTaskDetails(task);
        } else {
            alert('Không tìm thấy công việc này trong danh sách hiện tại.');
        }
    };

    const handleCloseTaskDetailModal = () => {
        setShowTaskDetailModal(false);
        setSelectedTaskForDetail(null);
        setCurrentUserPermissions({ isManagerOrAdmin: false, isAssignee: false });
        setActionError(null);
        setTaskFiles([]);
        setIsUploading(false);
        setUploadProgress(0);
        setUploadError(null);
        setShowFileDeleteConfirm(false);
        setFileToDelete(null);
        setNewCommentText('');
        setIsAddingComment(false);
        setCommentError(null);
    };

    const handleTaskAction = async (newStatus, extraData = {}) => {
        if (!selectedTaskForDetail || !selectedTaskForDetail.teamId || !selectedTaskForDetail.id) {
            setActionError("Dữ liệu công việc không hợp lệ.");
            return;
        }
        if (!auth.currentUser) {
            setActionError("Bạn cần đăng nhập để thực hiện hành động này.");
            return;
        }

        setLoadingAction(true);
        setActionError(null);

        const { teamId, id: taskId } = selectedTaskForDetail;
        const updateData = {
            status: newStatus,
            ...extraData,
        };

        if (newStatus === 'approved') {
            updateData.approvedAt = new Date();
            updateData.approvedBy = { uid: auth.currentUser.uid, displayName: auth.currentUser.displayName || auth.currentUser.email };
        } else if (newStatus === 'rejected') {
            updateData.rejectedAt = new Date();
            updateData.rejectedBy = { uid: auth.currentUser.uid, displayName: auth.currentUser.displayName || auth.currentUser.email };
        } else if (newStatus === 'completed') {
            updateData.completedAt = new Date();
            updateData.progress = 100;
        }

        try {
            await updateTaskFunc({
                teamId,
                taskId: taskId.toString(),
                taskData: updateData
            });

            handleCloseTaskDetailModal();
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error("Lỗi khi cập nhật trạng thái công việc:", error);
            setActionError(error.message || "Đã xảy ra lỗi. Vui lòng thử lại.");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleApproveTask = () => handleTaskAction('approved');
    const handleRejectTask = () => handleTaskAction('rejected');
    const handleCompleteTask = () => handleTaskAction('completed');

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleUploadFile(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleUploadFile = async (file) => {
        if (!selectedTaskForDetail || !auth.currentUser) {
            setUploadError("Không thể tải lên tệp. Thiếu thông tin công việc hoặc người dùng.");
            return;
        }
        if (!currentUserPermissions.isManagerOrAdmin && !currentUserPermissions.isAssignee) {
            setUploadError("Bạn không có quyền tải tệp lên cho công việc này.");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setUploadError(null);

        const filePath = `teams/${selectedTaskForDetail.teamId}/tasks/${selectedTaskForDetail.id}/${Date.now()}_${file.name}`;
        const fileStorageRef = storageRef(storage, filePath);
        const uploadTask = uploadBytesResumable(fileStorageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error("Lỗi tải lên:", error);
                setUploadError(`Lỗi tải lên: ${error.code}`);
                setIsUploading(false);
                setUploadProgress(0);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const newFile = {
                        name: file.name,
                        url: downloadURL,
                        path: filePath,
                        size: file.size,
                        uploadedAt: new Date(),
                        uploadedBy: {
                            uid: auth.currentUser.uid,
                            displayName: auth.currentUser.displayName || auth.currentUser.email
                        }
                    };

                    const updatedFiles = [...taskFiles, newFile];
                    await updateTaskFunc({
                        teamId: selectedTaskForDetail.teamId,
                        taskId: selectedTaskForDetail.id.toString(),
                        taskData: { files: updatedFiles }
                    });

                    setTaskFiles(updatedFiles);
                    setIsUploading(false);
                    setUploadProgress(0);
                    setSelectedTaskForDetail(prev => ({ ...prev, files: updatedFiles }));

                } catch (error) {
                    console.error("Lỗi khi lấy URL tải xuống hoặc cập nhật Firestore:", error);
                    setUploadError("Không thể lưu thông tin tệp sau khi tải lên.");
                    setIsUploading(false);
                    setUploadProgress(0);
                    try {
                        await deleteObject(fileStorageRef);
                        console.log("Đã xóa tệp mồ côi khỏi Storage.");
                    } catch (deleteError) {
                        console.error("Lỗi khi xóa tệp mồ côi:", deleteError);
                    }
                }
            }
        );
    };

    const handleDownloadFile = (fileUrl, fileName) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDeleteFile = (file) => {
        if (!currentUserPermissions.isManagerOrAdmin && !currentUserPermissions.isAssignee) {
            setActionError("Bạn không có quyền xóa tệp này.");
            return;
        }
        setFileToDelete(file);
        setShowFileDeleteConfirm(true);
    };

    const cancelDeleteFile = () => {
        setShowFileDeleteConfirm(false);
        setFileToDelete(null);
    };

    const confirmDeleteFile = async () => {
        if (!fileToDelete || !selectedTaskForDetail || !auth.currentUser) {
            setActionError("Không thể xóa tệp. Thiếu thông tin.");
            setShowFileDeleteConfirm(false);
            return;
        }
        if (!currentUserPermissions.isManagerOrAdmin && !currentUserPermissions.isAssignee) {
            setActionError("Bạn không có quyền xóa tệp này.");
            setShowFileDeleteConfirm(false);
            return;
        }

        setLoadingAction(true);
        setActionError(null);

        try {
            const fileStorageRef = storageRef(storage, fileToDelete.path);
            await deleteObject(fileStorageRef);

            const updatedFiles = taskFiles.filter(f => f.path !== fileToDelete.path);
            await updateTaskFunc({
                teamId: selectedTaskForDetail.teamId,
                taskId: selectedTaskForDetail.id.toString(),
                taskData: { files: updatedFiles }
            });

            setTaskFiles(updatedFiles);
            setSelectedTaskForDetail(prev => ({ ...prev, files: updatedFiles }));
            setShowFileDeleteConfirm(false);
            setFileToDelete(null);

        } catch (error) {
            console.error("Lỗi khi xóa tệp:", error);
            setActionError(`Không thể xóa tệp: ${error.message}`);
        } finally {
            setLoadingAction(false);
            setShowFileDeleteConfirm(false);
        }
    };

    const handleReplyTo = (comment) => {
        setReplyTo(comment);
        setNewCommentText(`@${comment.createdBy?.displayName || comment.createdBy?.email || comment.author?.displayName || comment.author?.email || 'Người dùng'} `);
    };

    const handleCancelReply = () => {
        setReplyTo(null);
        setNewCommentText('');
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) {
            setCommentError("Vui lòng nhập nội dung bình luận.");
            return;
        }
        if (!selectedTaskForDetail || !auth.currentUser) {
            setCommentError("Không thể thêm bình luận. Thiếu thông tin công việc hoặc người dùng.");
            return;
        }
        if (!currentUserPermissions.isManagerOrAdmin && !currentUserPermissions.isAssignee) {
            setCommentError("Bạn không có quyền bình luận về công việc này.");
            return;
        }

        setIsAddingComment(true);
        setCommentError(null);

        const newComment = {
            id: Date.now().toString(),
            text: newCommentText.trim(),
            createdBy: {
                uid: auth.currentUser.uid,
                displayName: auth.currentUser.displayName || auth.currentUser.email,
                photoURL: auth.currentUser.photoURL || null
            },
            createdAt: new Date(),
            ...(replyTo ? { 
                replyTo: { 
                    id: replyTo.id,
                    text: replyTo.text.substring(0, 50) + (replyTo.text.length > 50 ? '...' : ''),
                    createdBy: {
                        uid: replyTo.createdBy?.uid || replyTo.author?.uid,
                        displayName: replyTo.createdBy?.displayName || replyTo.author?.displayName || replyTo.createdBy?.email || replyTo.author?.email
                    }
                } 
            } : {})
        };

        try {
            const existingComments = Array.isArray(selectedTaskForDetail.comments) 
                ? [...selectedTaskForDetail.comments] 
                : [];
            
            const updatedComments = [...existingComments, newComment];
            
            await updateTaskFunc({
                teamId: selectedTaskForDetail.teamId,
                taskId: selectedTaskForDetail.id.toString(),
                taskData: { comments: updatedComments }
            });

            setSelectedTaskForDetail(prev => ({
                ...prev,
                comments: updatedComments
            }));
            
            setNewCommentText('');
            setReplyTo(null);

        } catch (error) {
            console.error("Lỗi khi thêm bình luận:", error);
            setCommentError(`Không thể thêm bình luận: ${error.message}`);
        } finally {
            setIsAddingComment(false);
        }
    };

    //-----------------------------------------------------------------------

    const commonChartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 15, font: { size: 11 }}}, title: { display: true, font: { size: 16, weight: 'bold' }, padding: { top: 10, bottom: 15 }}, tooltip: { bodyFont: { size: 12 }, titleFont: { size: 13, weight: 'bold' }}}};
    const doughnutPieOptions = { ...commonChartOptions, plugins: { ...commonChartOptions.plugins, legend: { position: 'right' }, title: { ...commonChartOptions.plugins.title, text: 'Phân bố Công việc' }}, cutout: '60%' };
    const barOptions = { ...commonChartOptions, plugins: { ...commonChartOptions.plugins, title: { ...commonChartOptions.plugins.title, text: 'Tiến độ TB theo Phòng ban'}}, scales: { x: { grid: { display: false }}, y: { beginAtZero: true, max: 100, title: { display: true, text: 'Tiến độ (%)', font: { size: 12 }}, ticks: { stepSize: 20 }}}};
    const lineOptions = { ...commonChartOptions, plugins: { ...commonChartOptions.plugins, title: { ...commonChartOptions.plugins.title, text: `Công việc Hoàn thành (${selectedTimeRange === 'week' ? '7 Ngày Qua' : selectedTimeRange === 'month' ? '4 Tuần Qua' : '12 Tháng Qua'})`}}, scales: { x: { grid: { display: false }}, y: { beginAtZero: true, title: { display: true, text: 'Số lượng công việc', font: { size: 12 }}, ticks: { precision: 0 }}}, tension: 0.3, pointRadius: 4, pointHoverRadius: 6 };

    const statusColors = [
        'rgba(255, 159, 64, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(75, 192, 192, 0.4)',
        'rgba(75, 192, 192, 0.9)', 'rgba(255, 99, 132, 0.7)', 'rgba(108, 117, 125, 0.7)', 'rgba(220, 53, 69, 0.7)'
    ];
    const priorityColors = ['rgba(75, 192, 192, 0.7)', 'rgba(255, 206, 86, 0.7)', 'rgba(255, 99, 132, 0.7)'];
    const statusData = { labels: tasksByStatus.labels, datasets: [{ data: tasksByStatus.data, backgroundColor: statusColors, borderColor: statusColors.map(c => c.replace(/0\.\d+\)/, '1)')), borderWidth: 1 }]};
    const priorityData = { labels: tasksByPriority.labels, datasets: [{ data: tasksByPriority.data, backgroundColor: priorityColors, borderColor: priorityColors.map(c => c.replace('0.7', '1')), borderWidth: 1 }]};
    const teamProgressData = { labels: taskProgressByTeam.labels, datasets: [{ label: 'Tiến độ TB (%)', data: taskProgressByTeam.data, backgroundColor: 'rgba(54, 162, 235, 0.7)', borderColor: 'rgba(54, 162, 235, 1)', borderWidth: 1, barThickness: 'flex', maxBarThickness: 50 }]};
    const completionTrendData = { labels: taskCompletionTrend.labels, datasets: [{ label: 'Công việc hoàn thành', data: taskCompletionTrend.data, fill: true, backgroundColor: 'rgba(75, 192, 192, 0.2)', borderColor: 'rgba(75, 192, 192, 1)', borderWidth: 2 }]};

    const indexOfLastTask = currentPage * tasksPerPage;
    const indexOfFirstTask = indexOfLastTask - tasksPerPage;
    const currentTasks = filteredTasks.slice(indexOfFirstTask, indexOfLastTask);
    const totalPages = Math.ceil(filteredTasks.length / tasksPerPage);
    const tableColSpan = (selectedTeam === 'all' ? 13 : 12);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
                    <p className="ml-4 text-gray-600 dark:text-gray-400">Đang tải dữ liệu báo cáo...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative dark:bg-red-900/30 dark:border-red-600 dark:text-red-300" role="alert">
                        <strong className="font-bold">Lỗi!</strong>
                        <span className="block sm:inline"> {error}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16 pb-10">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tiêu đề và Bộ lọc */}
                <div className="mb-8 p-5 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Báo Cáo & Phân Tích</h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Tổng quan hiệu suất công việc và tiến độ.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button onClick={handleExportExcel} disabled={filteredTasks.length === 0} className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:focus:ring-offset-gray-800 ${filteredTasks.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <i className="fas fa-file-excel mr-2"></i> Xuất Excel
                            </button>
                            <button onClick={handleExportPDF} disabled={filteredTasks.length === 0} className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 ${filteredTasks.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <i className="fas fa-file-pdf mr-2"></i> Xuất PDF
                            </button>
                            <button onClick={handleExportDOCX} disabled={filteredTasks.length === 0} className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 ${filteredTasks.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <i className="fas fa-file-word mr-2"></i> Xuất Word
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Thời gian:</span>
                            <div className="flex rounded-md shadow-sm">
                                <button className={`px-3 py-1 rounded-l-md text-xs focus:z-10 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${selectedTimeRange === 'week' ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`} onClick={() => handleTimeRangeChange('week')}>Tuần</button>
                                <button className={`px-3 py-1 text-xs -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${selectedTimeRange === 'month' ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`} onClick={() => handleTimeRangeChange('month')}>Tháng</button>
                                <button className={`px-3 py-1 rounded-r-md text-xs -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${selectedTimeRange === 'year' ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`} onClick={() => handleTimeRangeChange('year')}>Năm</button>
                            </div>
                        </div>                        <div className="flex items-center space-x-2">
                            <label htmlFor="team-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">Phòng ban:</label>
                            <select id="team-filter" className="block w-full sm:w-auto pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-emerald-500 dark:focus:border-emerald-500" value={selectedTeam} onChange={handleTeamFilterChange} >
                                <option value="all">Tất cả phòng ban</option>
                                {userTeams && userTeams.map(team => (<option key={team.id} value={team.id}>{team.name}</option>))}
                            </select>
                        </div>

                        {/* Bộ lọc Tháng/Năm */}
                        <div className="flex items-center space-x-2">
                            <label htmlFor="month-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">Tháng:</label>
                            <select 
                                id="month-filter" 
                                className="block w-full sm:w-auto pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                value={selectedMonth} 
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                                    <option key={month} value={month}>Tháng {month}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <label htmlFor="year-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">Năm:</label>
                            <select 
                                id="year-filter" 
                                className="block w-full sm:w-auto pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                value={selectedYear} 
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            >
                                {Array.from({length: 5}, (_, i) => currentDate.getFullYear() - i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        {(isSystemAdmin || isTeamManager) && userListForDropdown.length > 0 && (
                            <div id="user-search-container" className="flex items-center space-x-2 relative">
                                <label htmlFor="user-search" className="text-sm font-medium text-gray-700 dark:text-gray-300">Cá nhân:</label>
                                <div className="relative">
                                    <div className="flex items-center">                                        <input
                                            id="user-search"
                                            type="text"
                                            placeholder="Tìm kiếm theo tên, email..."
                                            className="block w-64 pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-emerald-500 dark:focus:border-emerald-500 dark:placeholder-gray-400"
                                            value={userSearchTerm}
                                            onChange={handleUserSearchChange}
                                            onKeyDown={handleUserSearchKeyDown}
                                            onFocus={() => userSearchTerm && setShowUserSuggestions(true)}
                                        />
                                        {userSearchTerm && (
                                            <button
                                                onClick={handleClearUserSearch}
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                                title="Xóa tìm kiếm"
                                            >
                                                <i className="fas fa-times text-xs"></i>
                                            </button>
                                        )}
                                    </div>
                                      {/* Suggestions Dropdown */}
                                    {showUserSuggestions && filteredUserSuggestions.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {filteredUserSuggestions.map((user, index) => (
                                                <button
                                                    key={user.uid}
                                                    onClick={() => handleUserSelect(user)}
                                                    className={`w-full px-3 py-2 text-left text-sm focus:outline-none flex items-center justify-between ${
                                                        index === selectedSuggestionIndex
                                                            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-100'
                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                    }`}
                                                >
                                                    <div>
                                                        <div className="font-medium">{user.displayName}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                                        {user.teamName}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Selected User Display */}
                                {selectedUser !== 'all' && (
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
                                            <i className="fas fa-user mr-1"></i>
                                            {userListForDropdown.find(u => u.uid === selectedUser)?.displayName || 'Đã chọn'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Thống kê tổng hợp (Cards) */}
                <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-emerald-100 dark:bg-emerald-900/30 rounded-md p-2"><i className="fas fa-check-circle text-emerald-600 dark:text-emerald-400 text-lg"></i></div>                            <div className="ml-4">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Tỷ lệ hoàn thành</dt>
                                <dd className="text-xl font-semibold text-gray-900 dark:text-white">{(() => { 
                                    const nonCanceledTasks = filteredTasks.filter(t => t.status !== 'canceled').length; 
                                    const completedTasks = filteredTasks.filter(t => t.status === 'completed').length; 
                                    return nonCanceledTasks > 0 ? `${Math.round((completedTasks / nonCanceledTasks) * 100)}%` : '0%'; 
                                })()}</dd>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-2"><i className="fas fa-tasks text-blue-600 dark:text-blue-400 text-lg"></i></div>
                            <div className="ml-4">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                                    Tổng CV ({
                                        selectedUser !== 'all' ? 
                                            userListForDropdown.find(u => u.uid === selectedUser)?.displayName || 'Cá nhân đã chọn' :
                                            selectedTeam === 'all' ? 'Tổng' : 
                                            userTeams.find(t => t.id === selectedTeam)?.name || 'Đã chọn'
                                    })
                                </dt>
                                <dd className="text-xl font-semibold text-gray-900 dark:text-white">{filteredTasks.length}</dd>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/30 rounded-md p-2"><i className="fas fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 text-lg"></i></div>
                            <div className="ml-4">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Ưu tiên cao (Đang xử lý)</dt>
                                <dd className="text-xl font-semibold text-gray-900 dark:text-white">{filteredTasks.filter(t => t.priority === 'high' && t.status !== 'completed' && t.status !== 'canceled').length}</dd>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 bg-red-100 dark:bg-red-900/30 rounded-md p-2"><i className="fas fa-calendar-times text-red-600 dark:text-red-400 text-lg"></i></div>
                            <div className="ml-4">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Công việc quá hạn</dt>
                                <dd className="text-xl font-semibold text-gray-900 dark:text-white">{filteredTasks.filter(t => isTaskOverdue(t)).length}</dd>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lưới Biểu Đồ */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5"><div className="h-72 sm:h-80"><Line options={lineOptions} data={completionTrendData} /></div></div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5"><div className="h-72 sm:h-80"><Bar options={barOptions} data={teamProgressData} /></div></div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">Công việc theo Trạng thái</h2>
                        <div className="h-72 sm:h-80 flex items-center justify-center">{filteredTasks.length > 0 ? (<Doughnut options={{ ...doughnutPieOptions, plugins: { ...doughnutPieOptions.plugins, title: { display: false } } }} data={statusData} />) : (<p className="text-gray-500 dark:text-gray-400 text-sm">Không có dữ liệu</p>)}</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">Công việc theo Mức ưu tiên</h2>
                        <div className="h-72 sm:h-80 flex items-center justify-center">{filteredTasks.length > 0 ? (<Pie options={{ ...doughnutPieOptions, plugins: { ...doughnutPieOptions.plugins, title: { display: false } } }} data={priorityData} />) : (<p className="text-gray-500 dark:text-gray-400 text-sm">Không có dữ liệu</p>)}</div>
                    </div>
                </div>

                {/* Bảng Chi Tiết Công Việc */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        Chi Tiết Công Việc {
                            selectedUser !== 'all' ? 
                                `(${userListForDropdown.find(u => u.uid === selectedUser)?.displayName || 'Cá nhân đã chọn'})` :
                                selectedTeam !== 'all' ? 
                                    `(${userTeams.find(t => t.id === selectedTeam)?.name || 'Đã chọn'})` : 
                                    ''
                        }
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-10">STT</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tên CV</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider max-w-xs">Mô tả</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trạng Thái</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ưu Tiên</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Người TH</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ngày BĐ</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Thời gian</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hạn Chót</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ngày HT/Hủy</th>
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Người PD</th>
                                    {selectedTeam === 'all' && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phòng Ban</th>}
                                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tiến Độ (%)</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {currentTasks.length > 0 ? currentTasks.map((task, index) => {
                                    const dueDate = calculateDueDate(task.start_date, task.duration);
                                    const isCanceled = task.status === 'canceled';
                                    const isRejected = task.status === 'rejected';
                                    const isOverdue = !isCanceled && !isRejected && isTaskOverdue(task);
                                    const teamName = userTeams.find(t => t.id === task.teamId)?.name || 'N/A';
                                    const taskName = task.name || task.text || '';
                                    const description = task.description || '';
                                    const stt = indexOfFirstTask + index + 1;
                                    
                                    let completionOrCancelDate = 'N/A';
                                    if (task.status === 'completed' && task.completedAt) {
                                        completionOrCancelDate = formatDateTime(task.completedAt);
                                    } else if (task.status === 'canceled' && task.canceledAt) {
                                        completionOrCancelDate = formatDateTime(task.canceledAt);
                                    } else if (task.status === 'rejected' && task.rejectedAt) {
                                        completionOrCancelDate = formatDateTime(task.rejectedAt);
                                    }
                                    
                                    const assigneesDisplay = getAssigneeNamesJSX(task.assignees);
                                    const approverName = task.status === 'approved' ? (task.approverName || 'Không rõ') : '';

                                    return (
                                        <tr key={task.id || stt} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400">{stt}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                <button
                                                    onClick={() => handleViewTaskDetails(task)}
                                                    className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 font-medium focus:outline-none underline-offset-2 hover:underline"
                                                    title={`Xem chi tiết: ${taskName}`}
                                                >
                                                    {taskName}
                                                </button>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={description}>
                                                {description || <span className="italic text-gray-400 dark:text-gray-500">Không có</span>}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    isCanceled ? 'bg-gray-400 text-gray-800 dark:bg-gray-600 dark:text-gray-100' :
                                                    isRejected ? 'bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-200' :
                                                    isOverdue ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                                                    task.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                                                    task.status === 'inProgress' || task.status === 'in-progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' :
                                                    task.status === 'pendingApproval' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' :
                                                    task.status === 'approved' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300' :
                                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                                                }`}>
                                                    {isCanceled ? 'Đã hủy' : isRejected ? 'Đã từ chối' : isOverdue ? `Quá hạn` : getStatusLabel(task.status)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{getPriorityLabel(task.priority)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-[150px] truncate" title={getAssigneeNames(task.assignees)}>
                                                {assigneesDisplay}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatDate(task.start_date)}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300">{task.duration || 'N/A'}</td>
                                            <td className={`px-4 py-2 whitespace-nowrap text-sm ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-300'}`}>
                                                {formatDate(dueDate)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{completionOrCancelDate}</td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{approverName}</td>
                                            {selectedTeam === 'all' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{teamName}</td>}
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{task.progress || 0}%</td>
                                        </tr>
                                    )
                                }) : (
                                    <tr> <td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400"> Không có công việc nào để hiển thị. </td> </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {filteredTasks.length > tasksPerPage && (
                        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50">
                                    Trước
                                </button>
                                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50">
                                    Sau
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700 dark:text-gray-400">
                                        Hiển thị từ <span className="font-medium">{indexOfFirstTask + 1}</span> đến <span className="font-medium">{Math.min(indexOfLastTask, filteredTasks.length)}</span> trong tổng số <span className="font-medium">{filteredTasks.length}</span> công việc
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50">
                                            <span className="sr-only">Trước</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M12.707 5.293a1 1 010 1.414L10.586 10l7.293 3.293a1 1 01-1.414 1.414l-4-4a1 1 010-1.414l4-4a1 1 011.414-1.414z" clipRule="evenodd" /></svg>
                                        </button>
                                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300">
                                            Trang {currentPage} / {totalPages}
                                        </span>
                                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50">
                                            <span className="sr-only">Sau</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M7.293 14.707a1 1 010-1.414L10.586 10l7.293 6.707a1 1 01-1.414 1.414l-4-4a1 1 010-1.414l4-4a1 1 011.414-1.414z" clipRule="evenodd" /></svg>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Nhật Ký Hoạt Động */}
                {(userData && (userData.email === 'maxdevfs@gmail.com' || userData.email === 'ha.ttt@greenschool.edu.vn')) && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mt-8">
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            <i className="fas fa-history mr-2 text-blue-500 dark:text-blue-400"></i>
                            Nhật Ký Hoạt Động
                        </h2>
                        <div className="flex items-center">
                            <select
                                id="activity-task-filter"
                                className="block pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                defaultValue="all"
                            >
                                <option value="all">Tất cả công việc</option>
                                {filteredTasks.map(task => (
                                    <option key={task.id} value={task.id}>{task.text || task.name}</option>
                                ))}
                            </select>
                            <select
                                id="activity-type-filter"
                                className="block pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 rounded-md ml-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                defaultValue="all"
                            >
                                <option value="all">Tất cả hoạt động</option>
                                <option value="status_update">Chuyển trạng thái</option>
                                <option value="comment">Bình luận</option>
                                <option value="file_upload">Tải lên tệp tin</option>
                                <option value="file_delete">Xóa tệp tin</option>
                                <option value="assignment">Phân công</option>
                                <option value="task_create">Tạo công việc</option>
                                <option value="task_edit">Sửa công việc</option>
                                <option value="task_delete">Xóa công việc</option>
                            </select>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <ActivityLogTable filteredTasks={filteredTasks} teamActivityLogs={teamActivityLogs} handleViewTaskDetailsById={handleViewTaskDetailsById} />
                    </div>
                </div>
                )}
            </div>

            {/* Modal Chi Tiết Công Việc Nâng Cao */}
            {showTaskDetailModal && selectedTaskForDetail && (
                <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity dark:bg-opacity-80" aria-hidden="true" onClick={handleCloseTaskDetailModal}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">​</span>
                        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                            <div className="max-h-[80vh] overflow-y-auto">
                                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                            <i className="fas fa-tasks text-emerald-600 dark:text-emerald-400 text-xl"></i>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-2" id="modal-title">
                                                {selectedTaskForDetail.name || selectedTaskForDetail.text || "Chi tiết công việc"}
                                            </h3>
                                            {/* Vai trò của bạn trong công việc này */}
                                            {auth.currentUser && (
                                                (() => {
                                                    const role = getUserRoleInTask(selectedTaskForDetail, auth.currentUser);
                                                    if (!role) return null;
                                                    let color = 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
                                                    let icon = 'fas fa-user-tag';
                                                    if (role.toLowerCase().includes('phê duyệt')) { color = 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'; icon = 'fas fa-user-check'; }
                                                    else if (role.toLowerCase().includes('giám sát')) { color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'; icon = 'fas fa-user-shield'; }
                                                    else if (role.toLowerCase().includes('phối hợp')) { color = 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200'; icon = 'fas fa-user-friends'; }
                                                    else if (role.toLowerCase().includes('thực hiện')) { color = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'; icon = 'fas fa-user-check'; }
                                                    return (
                                                        <div className={`inline-flex items-center px-3 py-1 mb-2 rounded-full text-xs font-semibold ${color}`}
                                                            title="Vai trò của bạn trong công việc này">
                                                            <i className={`${icon} mr-2`}></i>
                                                            Bạn là: {role}
                                                        </div>
                                                    );
                                                })()
                                            )}
                                            <div className="mb-4">
                                                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-1">Mô tả</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                                                    {Linkify({ text: selectedTaskForDetail.description }) || <span className="italic">Không có mô tả</span>}
                                                </p>
                                            </div>
                                            <hr className="my-3 border-gray-200 dark:border-gray-600"/>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm text-gray-700 dark:text-gray-300 mb-4">
                                                <p><strong className="font-semibold w-28 inline-block">Trạng thái:</strong>
                                                    <span className={`ml-2 px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        selectedTaskForDetail.status === 'canceled' ? 'bg-gray-400 text-gray-800 dark:bg-gray-600 dark:text-gray-100' :
                                                        selectedTaskForDetail.status === 'rejected' ? 'bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-200' :
                                                        isTaskOverdue(selectedTaskForDetail) ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                                                        selectedTaskForDetail.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                                                        selectedTaskForDetail.status === 'inProgress' || selectedTaskForDetail.status === 'in-progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' :
                                                        selectedTaskForDetail.status === 'pendingApproval' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' :
                                                        selectedTaskForDetail.status === 'approved' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300' :
                                                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                                                    }`}>
                                                        {getStatusLabel(selectedTaskForDetail.status)}
                                                    </span>
                                                    {selectedTaskForDetail.status !== 'canceled' && selectedTaskForDetail.status !== 'rejected' && isTaskOverdue(selectedTaskForDetail) && <span className="ml-2 text-red-600 dark:text-red-400 font-bold">(Quá hạn)</span>}
                                                </p>
                                                <p><strong className="font-semibold w-28 inline-block">Ưu tiên:</strong> {getPriorityLabel(selectedTaskForDetail.priority)}</p>
                                                <p><strong className="font-semibold w-28 inline-block">Ngày bắt đầu:</strong> {formatDate(selectedTaskForDetail.start_date)}</p>
                                                <p><strong className="font-semibold w-28 inline-block">Thời gian:</strong> {selectedTaskForDetail.duration || 'N/A'} ngày</p>
                                                <p><strong className="font-semibold w-28 inline-block">Hạn chót:</strong> {formatDate(calculateDueDate(selectedTaskForDetail.start_date, selectedTaskForDetail.duration))}</p>
                                                <p><strong className="font-semibold w-28 inline-block">Tiến độ:</strong> {selectedTaskForDetail.progress || 0}%</p>
                                                {selectedTaskForDetail.status === 'approved' && selectedTaskForDetail.approvedAt && (
                                                    <p className="col-span-1 sm:col-span-2"><strong className="font-semibold w-28 inline-block">Ngày duyệt:</strong> {formatDate(selectedTaskForDetail.approvedAt, true)} bởi {selectedTaskForDetail.approverName || 'Không rõ'}</p>
                                                )}
                                                {selectedTaskForDetail.status === 'completed' && selectedTaskForDetail.completedAt && (
                                                    <p className="col-span-1 sm:col-span-2"><strong className="font-semibold w-28 inline-block">Ngày hoàn thành:</strong> {formatDate(selectedTaskForDetail.completedAt, true)}</p>
                                                )}
                                                {selectedTaskForDetail.status === 'rejected' && selectedTaskForDetail.rejectedAt && (
                                                    <p className="col-span-1 sm:cl-span-2"><strong className="font-semibold w-28 inline-block">Ngày từ chối:</strong> {formatDate(selectedTaskForDetail.rejectedAt, true)} bởi {selectedTaskForDetail.rejectedBy?.displayName || selectedTaskForDetail.rejectedBy?.email || 'N/A'}</p>
                                                )}
                                                {selectedTaskForDetail.status === 'canceled' && selectedTaskForDetail.canceledAt && (
                                                    <p className="col-span-1 sm:col-span-2"><strong className="font-semibold w-28 inline-block">Ngày hủy:</strong> {formatDate(selectedTaskForDetail.canceledAt, true)}</p>
                                                )}
                                                <p className="col-span-1 sm:col-span-2"><strong className="font-semibold w-28 inline-block">Phòng ban:</strong> {userTeams.find(t => t.id === selectedTaskForDetail.teamId)?.name || 'Không xác định'}</p>
                                            </div>
                                            <div className="mb-4">
                                                <strong className="font-semibold text-sm text-gray-700 dark:text-gray-300">Người thực hiện:</strong>
                                                <ul className="list-none mt-1 pl-0">
                                                    {formatAssigneesList(selectedTaskForDetail.assignees)}
                                                </ul>
                                            </div>
                                            <hr className="my-3 border-gray-200 dark:border-gray-600"/>

                                            <div className="mt-4">
                                                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Tệp đính kèm</h4>
                                                {(currentUserPermissions.isManagerOrAdmin || currentUserPermissions.isAssignee) && (
                                                    <div className="mb-3">
                                                        <input
                                                            type="file"
                                                            ref={fileInputRef}
                                                            onChange={handleFileSelect}
                                                            className="hidden"
                                                            disabled={isUploading}
                                                        />
                                                        <button
                                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                            disabled={isUploading}
                                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 dark:focus:ring-offset-gray-800"
                                                        >
                                                            <i className="fas fa-upload mr-2"></i> Tải lên tệp mới
                                                        </button>
                                                        {isUploading && (
                                                            <div className="mt-2">
                                                                <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                                                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                                                </div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Đang tải lên: {Math.round(uploadProgress)}%</p>
                                                            </div>
                                                        )}
                                                        {uploadError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{uploadError}</p>}
                                                    </div>
                                                )}

                                                {taskFiles.length > 0 ? (
                                                    <ul className="border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-200 dark:divide-gray-600 max-h-48 overflow-y-auto">
                                                        {taskFiles.map((file, index) => (
                                                            <li key={index} className="pl-3 pr-4 py-2 flex items-center justify-between text-sm">
                                                                <div className="w-0 flex-1 flex items-center">
                                                                    <i className="fas fa-paperclip text-gray-400 flex-shrink-0 h-4 w-4" aria-hidden="true"></i>
                                                                    <span className="ml-2 flex-1 w-0 truncate dark:text-gray-300" title={file.name}>
                                                                        {file.name}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">({formatFileSize(file.size)})</span>
                                                                </div>
                                                                <div className="ml-4 flex-shrink-0 space-x-2">
                                                                    <button onClick={() => handleDownloadFile(file.url, file.name)} className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 focus:outline-none">
                                                                        <i className="fas fa-download" title="Tải xuống"></i>
                                                                    </button>
                                                                    {(currentUserPermissions.isManagerOrAdmin || currentUserPermissions.isAssignee) && (
                                                                        <button onClick={() => handleDeleteFile(file)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 focus:outline-none" disabled={loadingAction}>
                                                                            <i className="fas fa-trash-alt" title="Xóa"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Chưa có tệp nào được đính kèm.</p>
                                                )}
                                            </div>
                                            <hr className="my-4 border-gray-200 dark:border-gray-600"/>

                                            {/* Comments Section */}
                                            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">Bình luận</h4>
                                                <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                                    {selectedTaskForDetail.comments && selectedTaskForDetail.comments.length > 0 ? (
                                                        selectedTaskForDetail.comments.map((comment, index) => (
                                                            <div key={index} className="flex items-start space-x-2 text-sm">
                                                                <img
                                                                    src={comment.createdBy?.photoURL || comment.author?.photoURL || 
                                                                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                                        ((comment.createdBy?.displayName || comment.createdBy?.email || comment.author?.displayName || comment.author?.email || 'U').substring(0, 1))
                                                                      )}&background=random`}
                                                                    alt={(comment.createdBy?.displayName || comment.author?.displayName || 'Avatar')}
                                                                    className="w-6 h-6 rounded-full flex-shrink-0"
                                                                />
                                                                <div className="flex-grow bg-gray-100 dark:bg-gray-700 rounded-md p-2">
                                                                    <div className="flex justify-between items-baseline mb-1">
                                                                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                                                                            {(comment.createdBy && (comment.createdBy.displayName || comment.createdBy.email)) || 
                                                                             (comment.author && (comment.author.displayName || comment.author.email)) ||
                                                                             (typeof comment.createdBy === 'string' ? comment.createdBy : 
                                                                              typeof comment.author === 'string' ? comment.author : 'Người dùng không xác định')}
                                                                        </span>
                                                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                            {formatDateTime(comment.createdAt || comment.timestamp || comment.date)}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-gray-700 dark:text-gray-300 break-words whitespace-pre-line"><Linkify text={comment.text} /></p>
                                                                    {comment.replyTo && (
                                                                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                                                            Trả lời: <span className="font-semibold">{comment.replyTo.createdBy.displayName || comment.replyTo.createdBy.email || 'Người dùng'}</span> - "{comment.replyTo.text}"
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleReplyTo(comment)}
                                                                        className="mt-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 focus:outline-none"
                                                                    >
                                                                        Trả lời
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Chưa có bình luận nào.</p>
                                                    )}
                                                </div>
                                                {/* Add Comment Form */}
                                                {(currentUserPermissions.isManagerOrAdmin || currentUserPermissions.isAssignee) && (
                                                    <div className="mt-4">
                                                        {replyTo && (
                                                            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                                                Trả lời: <span className="font-semibold">{replyTo.createdBy.displayName || replyTo.createdBy.email || 'Người dùng'}</span> - "{replyTo.text}"
                                                                <button
                                                                    onClick={handleCancelReply}
                                                                    className="ml-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 focus:outline-none"
                                                                >
                                                                    Hủy
                                                                </button>
                                                            </div>
                                                        )}
                                                        <textarea
                                                            rows="2"
                                                            className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                                                            placeholder="Thêm bình luận..."
                                                            value={newCommentText}
                                                            onChange={(e) => setNewCommentText(e.target.value)}
                                                            disabled={isAddingComment}
                                                        ></textarea>
                                                        {commentError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{commentError}</p>}
                                                        <button
                                                            onClick={handleAddComment}
                                                            disabled={isAddingComment || !newCommentText.trim()}
                                                            className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {isAddingComment ? (
                                                                <><svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Đang gửi...</>
                                                            ) : (
                                                                <><i className="fas fa-paper-plane mr-1.5"></i> Gửi bình luận</>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Modal Footer - Actions */}
                            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 px-6 py-4 rounded-b-lg flex flex-col sm:flex-row justify-end items-center space-y-3 sm:space-y-0 sm:space-x-3">
                                {actionError && <p className="text-sm text-red-600 dark:text-red-400 w-full sm:w-auto text-center sm:text-left">{actionError}</p>}
                                {loadingAction && <div className="flex items-center justify-center w-full sm:w-auto"><div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-emerald-500"></div><span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Đang xử lý...</span></div>}

                                {/* Fix: Corrected button visibility logic */}
                                {!loadingAction && (
                                    <>
                                        {/* Approve/Reject Buttons (Manager/Admin only, for pending tasks) */}
                                        {currentUserPermissions.isManagerOrAdmin && selectedTaskForDetail.status === 'pendingApproval' && (
                                            <>
                                                <button
                                                    onClick={handleApproveTask}
                                                    className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-md text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:focus:ring-offset-gray-800 transition-all duration-300 ease-in-out transform hover:translate-y-[-1px]"
                                                >
                                                    <i className="fas fa-check-circle mr-2 text-white/90"></i> Phê duyệt
                                                </button>
                                                <button
                                                    onClick={handleRejectTask}
                                                    className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-all duration-300 ease-in-out"
                                                >
                                                    <i className="fas fa-times-circle mr-2 text-red-500 dark:text-red-400"></i> Từ chối
                                                </button>
                                            </>
                                        )}

                                        {/* Complete Button (Assignee only, for in-progress tasks) */}
                                        {currentUserPermissions.isAssignee && (selectedTaskForDetail.status === 'inProgress' || selectedTaskForDetail.status === 'in-progress') && (
                                            <button
                                                onClick={handleCompleteTask}
                                                className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-md text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 transition-all duration-300 ease-in-out transform hover:translate-y-[-1px]"
                                            >
                                                <i className="fas fa-flag-checkered mr-2 text-white/90"></i> Hoàn thành
                                            </button>
                                        )}

                                        {/* Close Button (Always visible) */}
                                        <button
                                            type="button"
                                            onClick={handleCloseTaskDetailModal}
                                            className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 shadow-md rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-offset-gray-800 transition-all duration-300 ease-in-out"
                                        >
                                            <i className="fas fa-times mr-2 opacity-70"></i> Đóng
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showFileDeleteConfirm && fileToDelete && (
                <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="delete-confirm-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity dark:bg-opacity-80" aria-hidden="true" onClick={cancelDeleteFile}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">​</span>
                        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                        <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-xl"></i>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="delete-confirm-title">
                                            Xác nhận xóa tệp
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Bạn có chắc chắn muốn xóa tệp <strong className="break-all">{fileToDelete.name}</strong> không? Hành động này không thể hoàn tác.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={confirmDeleteFile}
                                    disabled={loadingAction}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                >
                                    {loadingAction ? 'Đang xóa...' : 'Xóa'}
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelDeleteFile}
                                    disabled={loadingAction}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Reports;
